import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { DataService } from '../../Services/dataService';
import AutoComplete from '../AutoComplete';
import { certificateAmount, emptyModeRules, reimbursementAmount, remoteAmount } from '../CourierModeCharges/modeChargeMath';

/** Left-side label. Click opens why the amount exists and which table it comes from. */
const ExplainToggle = ({ label, why, from }) => {
    const [open, setOpen] = useState(false);
    const canOpen = Boolean(why || from);
    return (
        <div>
            <button
                type="button"
                onClick={() => canOpen && setOpen((v) => !v)}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    color: 'inherit',
                    font: 'inherit',
                    fontWeight: 600,
                    textAlign: 'left',
                    cursor: canOpen ? 'pointer' : 'default',
                }}
            >
                {canOpen && (
                    <span
                        style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            background: open ? '#dbeafe' : '#f1f5f9',
                            color: '#1d4ed8',
                            fontSize: 10,
                            lineHeight: '16px',
                            textAlign: 'center',
                            flexShrink: 0,
                        }}
                    >
                        {open ? '▾' : '▸'}
                    </span>
                )}
                <span>{label}</span>
            </button>
            {open && (
                <div
                    style={{
                        marginTop: 6,
                        padding: '8px 10px',
                        background: '#f8fafc',
                        borderRadius: 8,
                        borderLeft: '3px solid #2563eb',
                        fontWeight: 400,
                        fontSize: '0.72rem',
                        lineHeight: 1.45,
                        color: '#334155',
                    }}
                >
                    {why && (
                        <div style={{ marginBottom: from ? 6 : 0 }}>
                            <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: 2 }}>Why</div>
                            {why}
                        </div>
                    )}
                    {from && (
                        <div>
                            <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: 2 }}>From</div>
                            {from}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/** Format charge label cell. Formula stays closed until the name is clicked. */
const ChargeLabel = ({ label, hint, from }) => (
    <td className="text-muted" style={{ verticalAlign: 'top' }}>
        <ExplainToggle label={label} why={hint} from={from} />
    </td>
);

/** Infer fuel % from amount / freight base when API % not present */
const inferFuelPercent = (fuelAmt, freightAmt) => {
    const fuel = parseFloat(fuelAmt) || 0;
    const frt = parseFloat(freightAmt) || 0;
    if (fuel <= 0 || frt <= 0) return null;
    return money((fuel / frt) * 100, 2);
};

/** Pull fuel % from courier/land additional_charges or rate fields */
const COURIER_CARRIERS = ['fedex', 'ups', 'dhl'];

// Courier Vol M/E is cut (not rounded) to 3 decimals, same as backend; epsilon guards float artifacts
const truncateTo3 = (value) => Math.floor(Number(value) * 1000 + 1e-6) / 1000;
// Chargeable round-off for every mode, same as backend rounding_helper: nearest whole number
// (51.4 → 51, 51.5 / 51.6 → 52). `minimum` keeps tiny shipments billable (0.5 kg / 1 CBM).
// Door-to-door CAF is charged on the Round Off amount (not the unrounded total with margins);
// Total Freight = Round Off + CAF, kept to 2 decimals (e.g. 453 + 22.65 = 475.65). Same as FreightCalculator.
const computeDoorCaf = (totalWithMargins, cafPct) => {
    const roundOff = Math.round(Number(totalWithMargins) || 0);
    const caf = (roundOff * (Number(cafPct) || 0)) / 100;
    return { roundOff, caf, totalFreight: Number(money((roundOff + caf), 2)) };
};

// Tier margin lookup with the same rules as backend get_profit_margin, so RateChecker and the retailer
// Freight Calculator land on the same band: a total or goods value outside the table uses the top band.
const pickTierMargin = (profitMargins, tierIndex, total, goodsValue) => {
    const tierName = tierIndex === 1 ? 'Tier_2' : tierIndex === 2 ? 'Tier_3' : 'Tier_1';
    const tiers = Array.isArray(profitMargins) ? profitMargins : [];
    const tier = tiers.find((m) => m && m.freight_type === tierName);
    const bands = Array.isArray(tier?.freight_data) ? tier.freight_data.filter((b) => b && typeof b === 'object') : [];
    if (!bands.length) return null;
    const topBand = bands.reduce((top, b) => (Number(b.freight_from || 0) > Number(top.freight_from || 0) ? b : top), bands[0]);
    const band = bands.find((b) => total >= Number(b.freight_from || 0) && total <= Number(b.freight_to || 999999)) || topBand;
    const goodsBands = Array.isArray(band?.value_of_goods) ? band.value_of_goods.filter((g) => g && typeof g === 'object') : [];
    if (!goodsBands.length) return null;
    const topGoods = goodsBands.reduce((top, g) => (Number(g.max_goods || 0) > Number(top.max_goods || 0) ? g : top), goodsBands[0]);
    const goods = goodsBands.find((g) => goodsValue >= Number(g.min_goods || 0) && goodsValue <= Number(g.max_goods || 9999999)) || topGoods;
    return { marginPct: Number(goods?.profit) || 0, minMarginAmt: Number(goods?.min_profit) || 0 };
};

const roundOffChargeable = (value, minimum = 0) => {
    const n = Number(value) || 0;
    const rounded = Math.round(Number(money(n, 3)));
    return n > 0 ? Math.max(rounded, minimum) : rounded;
};

const money = (value, digits = 2) => {
    const n = Number(value);
    return (Number.isFinite(n) ? n : 0).toFixed(digits);
};

class QuoteErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) {
        return { error };
    }
    componentDidUpdate(prev) {
        if (prev.resetKey !== this.props.resetKey && this.state.error) {
            this.setState({ error: null });
        }
    }
    render() {
        if (this.state.error) {
            return (
                <div className="alert alert-warning mt-3 mb-0">
                    This quote could not be shown for the selected location. Change the location or run the rate again. The rest of the page stays available.
                </div>
            );
        }
        return this.props.children;
    }
}

// Courier: every product, every package. Each package takes the greater of its
// weight-with-margin and its volume kg, multiplied by how many of that package there are.
const packageChargeable = (products, wPct, vPct, kgPerCbm) => {
    let total = 0;
    (products || []).forEach((prod) => {
        const prodQty = parseInt(prod.quantity, 10) || 1;
        (prod.packages || []).forEach((pkg) => {
            const count = (parseInt(pkg.count, 10) || 1) * prodQty;
            const weightMe = (parseFloat(pkg.weight) || 0) * (1 + wPct / 100);
            const vol = ((parseFloat(pkg.length) || 0) * (parseFloat(pkg.width) || 0) * (parseFloat(pkg.height) || 0)) / 1000000;
            const volWt = vol * (1 + vPct / 100) * kgPerCbm;
            total += Math.max(weightMe, volWt) * count;
        });
    });
    return total;
};

const courierPackageChargeable = (products, wPct, vPct) => packageChargeable(products, wPct, vPct, 200);
const landPackageChargeable = (products, wPct, vPct) => packageChargeable(products, wPct, vPct, 250);

const packingKind = (raw) => {
    const s = String(raw || '').toLowerCase().replace(/packaging/g, 'packing').replace(/_/g, ' ');
    if (!s || s.includes('not required')) return null;
    if (s.includes('carton') || s.includes('export')) return 'carton';
    if (s.includes('pallet')) return 'pallet';
    if (s.includes('crate')) return 'crate';
    if (s.includes('wooden')) return 'wooden';
    return null;
};

const sixSideSqm = (lCm, wCm, hCm) => {
    const l = lCm / 100;
    const w = wCm / 100;
    const h = hCm / 100;
    return 2 * ((l * w) + (w * h) + (h * l));
};

const PACKING_GROWTH = {
    carton: { l: 1, w: 1, h: 1 },
    pallet: { l: 1, w: 1, h: 15 },
    crate: { l: 6, w: 6, h: 15 },
    wooden: { l: 6, w: 6, h: 15 },
};

const grownPackage = (l, w, h, wt, kind) => {
    const grow = PACKING_GROWTH[kind];
    const nl = l + grow.l;
    const nw = w + grow.w;
    const nh = h + grow.h;
    let extraKg = 0;
    if (kind === 'carton') {
        extraKg = ((nl * nw * nh) / 1000000) / 0.14;
    } else if (kind === 'pallet') {
        extraKg = (((nl / 100) * (nw / 100)) / 0.25) * 4;
    } else if (kind === 'crate') {
        extraKg = sixSideSqm(nl, nw, nh) * 3;
    } else if (kind === 'wooden') {
        extraKg = sixSideSqm(nl, nw, nh) * 5;
    }
    return { l: nl, w: nw, h: nh, wt: wt + extraKg };
};

const freightAmountOf = (block) => {
    if (!block || typeof block !== 'object') return 0;
    const keys = ['fregiht_amount', 'fregiht', 'freight', 'fedex_fregiht', 'ups_fregiht', 'dhl_fregiht', 'fedex_freight', 'ups_freight', 'dhl_freight'];
    for (const key of keys) {
        const n = parseFloat(block[key]);
        if (Number.isFinite(n)) return n;
    }
    return 0;
};

const freightMapOf = (root) => {
    const map = {};
    if (!root || typeof root !== 'object') return map;
    ['port_delivery', 'door_delivery', 'consolidation_delivery'].forEach((key) => {
        map[key] = freightAmountOf(root[key]);
    });
    Object.keys(root).forEach((key) => {
        if (key.endsWith('_courier_rates') && Array.isArray(root[key])) {
            root[key].forEach((rate, index) => {
                map[`${key}:${index}`] = freightAmountOf(rate);
            });
        }
    });
    return map;
};

const packingChargeFor = (data, serviceKey) => {
    const material = Number(data?.packingMaterial) || 0;
    const delta = Number(data?.freightDelta?.[serviceKey]) || 0;
    return material + (delta > 0 ? delta : 0);
};

const lineNum = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
};

const chargeTotalBeforeProfit = (block, mode, serviceKey) => {
    if (!block || typeof block !== 'object') return 0;
    const key = String(serviceKey || '');
    if (mode === 'COURIER' || key.includes('courier_rates')) {
        if (block.total_amount !== undefined && block.total_amount !== null && block.total_amount !== '') {
            return lineNum(block.total_amount);
        }
        const prefix = key.split('_')[0];
        return [
            'be_fee', 'admin_fee', 'transport_store_to_s2d', 'handling_in_out',
            `${prefix}_fregiht`, `${prefix}_freight`,
            `${prefix}_fuel_charges`, `${prefix}_govt_tax_amount`,
            'additional_surcharge_amount', 'over_30_charge', 'over_30_kgs',
            'large_package_surcharge_amount', 'over_max_surcharge_amount',
            'additional_handling_charges', 'peak_surcharge', 'express_freight_shipment_amount',
            'express_shipment_amount', 'shipment_amount', 'additional_shipment_amount',
            'ups_additional_amount', 'ups_large_package_surcharge_amount', 'ups_over_max_surcharge_amount',
            'peak_demand_surcharge',
        ].reduce((sum, field) => sum + lineNum(block[field]), 0);
    }
    if (mode === 'LAND') {
        const fields = key === 'consolidation_delivery'
            ? ['fregiht', 'freight', 'pallet_charges', 'handling_in_out_express', 'transport_store_to_s2d', 'silla_border', 'batha_border', 'final_destination_charge', 'certificate_of_origin']
            : ['be_fee', 'admin_fee', 'transport_store_to_s2d', 'handling_in_out', 'fregiht', 'freight', 'fuel_charges', 'peak_surcharge', 'additional_surcharge_amount'];
        return fields.reduce((sum, field) => sum + lineNum(block[field]), 0);
    }
    if (mode === 'SEA') {
        let total = lineNum(block.bl_fee || block.b_l_fee) + lineNum(block.customs_docs) + lineNum(block.weightment_charges)
            + lineNum(block.transport_store_to_s2d) + lineNum(block.transport_s2d_to_seaport)
            + lineNum(block.handling_in_out) + lineNum(block.fregiht_amount) + lineNum(block.additional_fee);
        if (key === 'door_delivery') {
            total += lineNum(block.NVOCC_charges_fixed) + lineNum(block.NVOCC_charges_per_cmb || block.NVOCC_charges_per_cbm)
                + lineNum(block.port_charges) + lineNum(block.customs_clearance) + lineNum(block.bank_charges)
                + lineNum(block.quarantine_fee) + lineNum(block.transport_tariff) + lineNum(block.destination_inland_amount);
        }
        return total;
    }
    let total = lineNum(block.awb_fee) + lineNum(block.airline_handling) + lineNum(block.customs_docs || block.customs)
        + lineNum(block.transport_store_to_s2d || block.transport_1) + lineNum(block.transport_s2d_to_apt || block.transport_2)
        + lineNum(block.min_handling_in_out || block.handling_in_out)
        + lineNum(block.fregiht_amount || block.freight || block.base_freight)
        + lineNum(block.fuel_amount || block.fuel);
    if (key === 'door_delivery') {
        total += lineNum(block.door_airline_charges || block.dthc) + lineNum(block.deliver_order_fee || block.do_fee)
            + lineNum(block.customs_clearance) + lineNum(block.bank_charges || block.bank)
            + lineNum(block.quarantine_fee || block.quarantine) + lineNum(block.transport_tariff || block.transport)
            + lineNum(block.destination_inland_amount || block.inland);
    }
    return total;
};

const roundOffThroughMargin = (block, mode, serviceKey, goodsValue, profitMargins, tierIndex) => {
    const total = chargeTotalBeforeProfit(block, mode, serviceKey);
    const tierMargin = pickTierMargin(profitMargins, tierIndex, total, goodsValue);
    const marginPct = tierMargin ? tierMargin.marginPct : 30;
    const minMarginAmt = tierMargin ? tierMargin.minMarginAmt : 0;
    let profit = total * (marginPct / 100);
    if (!Number.isFinite(profit) || profit < minMarginAmt) profit = minMarginAmt;
    const rounded = Math.round(total + profit);
    return Number.isFinite(rounded) ? rounded : 0;
};

const getFuelPercentFromData = (details, data, courierPrefix) => {
    if (!details && !data) return null;
    const d = details || {};
    // Courier: only this carrier's tariff, otherwise one carrier's % (e.g. FedEx 53.25%) leaks onto UPS/DHL
    const candidates = COURIER_CARRIERS.includes(courierPrefix) ? [
        d[`${courierPrefix}_fuel_percentage`],
        d.fuel_percentage,
        data?.[`courier_country_${courierPrefix}_prices`]?.[0]?.additional_charges?.fuel_charges,
    ] : [
        d.fuel_percentage,
        d.fuel_percent,
        d[`${courierPrefix}_fuel_percentage`],
        d.additional_charges?.fuel_charges,
        data?.get_tnt_land_freight?.additional_charges?.fuel_charges,
        data?.air_freight?.additional_charges?.fuel_charges,
        data?.air_freight?.fuel_percentage,
        data?.courier_country_fedex_prices?.[0]?.additional_charges?.fuel_charges,
        data?.courier_country_ups_prices?.[0]?.additional_charges?.fuel_charges,
        data?.courier_country_dhl_prices?.[0]?.additional_charges?.fuel_charges,
    ];
    for (const c of candidates) {
        const n = parseFloat(c);
        if (!isNaN(n) && n > 0) return n;
    }
    return null;
};

const formatPackingHint = (meta) => {
    if (!meta) return null;
    const lines = Array.isArray(meta.lines) && meta.lines.length
        ? meta.lines
        : [{ cbm: meta.cbm, area: meta.area, qty: meta.qty, dims: meta.dims, weight: meta.weight }];
    return lines.filter((line) => line && typeof line === 'object').map((line) => {
        const size = line.dims ? `${line.dims} cm` : '';
        const wt = line.weight != null && line.weight !== '' ? `, ${line.weight} kg` : '';
        if (meta.method === 'cbm') {
            const cbm = typeof line.cbm === 'number' ? money(line.cbm, 4) : line.cbm;
            return `${size}${wt}, CBM ${cbm} × AED ${meta.rate}/CBM (min ${meta.min}) × ${line.qty} pkg`;
        }
        const area = typeof line.area === 'number' ? money(line.area, 3) : line.area;
        return `${size}${wt}, surface ${area} m² × AED ${meta.rate}/m² (min ${meta.min}) × ${line.qty} pkg`;
    }).join(' + ');
};

/** Package count, plus dimensions only (L × W × H) for the Dims row */
const summarizePackages = (products) => {
    const lines = [];
    let count = 0;
    (products || []).forEach((p) => {
        if (!p || typeof p !== 'object') return;
        (p.packages || []).forEach((pkg) => {
            if (!pkg || typeof pkg !== 'object') return;
            const n = parseInt(pkg.count, 10) || 0;
            count += n;
            const dims = [pkg.length, pkg.width, pkg.height].filter((v) => v !== '' && v != null).join(' × ');
            if (dims) lines.push(dims);
        });
    });
    return { count, text: lines.join(' | ') };
};

const placeName = (node) => node?.city_name || node?.name || '';
const cityPair = (near) => {
    const from = placeName(near?.originResult);
    const to = placeName(near?.destinationResult);
    return from && to ? `${from} → ${to}` : '';
};

/** Freight formula from the tariff row the quote actually used. No invented rate or zone. */
const explainFreight = (key, amount, ctx) => {
    const { data, details, serviceType, chargeableCbm, chargeableKg } = ctx || {};
    if (!data || !Number.isFinite(amount)) return null;
    const lower = String(key).toLowerCase();
    const service = String(serviceType || '');

    const seaRow = () => {
        if (service.toLowerCase().includes('consol')) {
            return { row: data.sea_consolidation_freight, route: cityPair(data.near_city_door) };
        }
        if (service === 'Door to Door') {
            return { row: data.sea_freight_door, route: cityPair(data.near_city_door) };
        }
        return { row: data.sea_freight, route: cityPair(data.near_city) || cityPair(data.city) };
    };

    if (lower === 'fregiht_amount' || (lower === 'freight' && data.sea_freight)) {
        const picked = seaRow();
        const row = picked.row;
        const rate = Number(row?.rate_per_cmb);
        if (!row || !Number.isFinite(rate) || rate <= 0) return null;
        const minCbm = Number(row.minimum_amount);
        let cbmUsed = Number(chargeableCbm);
        let minApplied = false;
        const applyMinimum = !service.toLowerCase().includes('consol');
        if (applyMinimum && Number.isFinite(minCbm) && Number.isFinite(cbmUsed) && minCbm > cbmUsed) {
            cbmUsed = minCbm;
            minApplied = true;
        }
        const extra = Number(details?.additional_fee ?? row.additional_fee);
        const extraUsed = Number.isFinite(extra) ? extra : 0;
        const base = amount - extraUsed;
        const parts = [];
        if (picked.route) parts.push(`Route ${picked.route}`);
        if (Number.isFinite(cbmUsed) && cbmUsed > 0) {
            const factor = base / (cbmUsed * rate);
            if (Number.isFinite(factor)) {
                parts.push(`${cbmUsed} CBM × USD ${rate}/CBM × ${Number(money(factor, 4))} (USD to AED) = AED ${money(base, 2)}${minApplied ? ' (minimum CBM applied)' : ''}`);
            }
        }
        if (extraUsed) parts.push(`includes additional fee ${money(extraUsed, 2)}`);
        if (!parts.length) return null;
        const factorNote = Number.isFinite(cbmUsed) && cbmUsed > 0
            ? (() => {
                const factor = base / (cbmUsed * rate);
                if (!Number.isFinite(factor)) return '';
                const shown = Number(money(factor, 4));
                return Math.abs(shown - 3.685) < 0.001
                    ? ` ${shown} is the fixed USD to AED conversion inside the sea quote. It is not a destination charge.`
                    : ` ${shown} is the USD to AED conversion applied to this sea rate.`;
            })()
            : '';
        return {
            label: 'Freight',
            hint: parts.join(' · '),
            from: `${picked.route ? `Sea freight rate for ${picked.route}. ` : 'Sea freight rate table. '}USD ${rate} per CBM is the destination sea rate.${factorNote}${minApplied ? ` Minimum CBM ${minCbm} on that rate row replaced the smaller chargeable CBM.` : ''}${extraUsed ? ` Additional fee ${money(extraUsed, 2)} on the same rate row is already inside this freight amount.` : ''}`,
        };
    }

    const carrier = lower.startsWith('fedex') ? 'fedex' : lower.startsWith('ups') ? 'ups' : lower.startsWith('dhl') ? 'dhl' : null;
    if (carrier && (lower.includes('fregiht') || lower.includes('freight'))) {
        const prices = data[`courier_country_${carrier}_prices`]?.[0];
        const band = prices?.courierservices?.[0];
        const rate = Number(band?.zone_price);
        if (!prices || !Number.isFinite(rate)) return null;
        const zone = prices.zone != null && prices.zone !== '' ? `Zone ${prices.zone}` : '';
        const bandKg = band.kgs != null && band.kgs !== '' ? `kg band ${band.kgs}` : '';
        const kg = Number(prices.weight_kgs != null ? prices.weight_kgs : chargeableKg);
        const head = [zone, bandKg].filter(Boolean).join(', ');
        let how = [head, `rate ${rate}`].filter(Boolean).join(', ');
        if (Number.isFinite(kg) && kg > 0 && Math.abs(amount - Math.round(rate * kg)) < 0.02 && Math.abs(amount - rate) > 0.02) {
            how = [head, `${rate} × ${kg} kg = ${Math.round(rate * kg)}`].filter(Boolean).join(', ');
        } else if (Math.abs(amount - rate) < 0.02) {
            how = [head, `flat rate ${rate}`].filter(Boolean).join(', ');
        }
        return {
            label: 'Freight',
            hint: how,
            from: `${carrier.toUpperCase()} courier zone price table. ${head || 'The matched kg band'} is the row selected for this weight.`,
        };
    }

    if ((lower === 'fregiht' || lower === 'freight') && (data.get_tnt_land_freight || data.get_tnt_consolidation_freight)) {
        const svc = service.toLowerCase().includes('consol')
            ? data.get_tnt_consolidation_freight
            : data.get_tnt_land_freight;
        const price = Number(svc?.destination_price?.price);
        if (!svc || !Number.isFinite(price)) return null;
        const band = svc.kgs != null ? svc.kgs : svc.cbm;
        const wt = Number(svc.weight_kgs != null ? svc.weight_kgs : chargeableKg);
        const bandLabel = band != null && band !== '' ? `Band ${band}` : '';
        let how = [bandLabel, `rate ${price}`].filter(Boolean).join(', ');
        if (Number.isFinite(wt) && wt > 0 && Math.abs(amount - (price * wt)) < 0.05 && Math.abs(amount - price) > 0.05) {
            how = [bandLabel, `${price} × ${wt} kg = ${money((price * wt), 2)}`].filter(Boolean).join(', ');
        } else if (Math.abs(amount - price) < 0.05) {
            how = [bandLabel, `flat rate ${price}`].filter(Boolean).join(', ');
        }
        return {
            label: 'Freight',
            hint: how,
            from: `Land destination price table${bandLabel ? ` (${bandLabel})` : ''}. The quote uses that band's rate for this chargeable weight.`,
        };
    }

    return null;
};

const formatAttrHint = (meta) => {
    if (!meta) return null;
    const parts = [];
    if (meta.dg_declaration > 0) parts.push(`DG Decl. ${meta.dg_declaration}`);
    if (meta.dangerous_goods_fee > 0) parts.push(`DG Fee ${meta.dangerous_goods_fee}`);
    if (meta.dg_packing > 0) parts.push(`DG Pack ${meta.dg_packing}`);
    if (meta.airline_handling > 0) {
        parts.push(`Handling ${meta.airline_handling}/kg × Ch.Wt ${meta.chargeableWeight}`);
    }
    parts.push(`mode ${meta.mode}`);
    return parts.join(' + ');
};

/** Shared packing + attribute rows with dynamic formula hints */
const recordEntries = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
    return Object.entries(value);
};

const renderPackingAndAttributeRows = (data, asTableRows = true, freightDiff = 0, freightPair = null) => {
    const safeData = data && typeof data === 'object' ? data : {};
    const packingMeta = safeData.packingBreakdownMeta && typeof safeData.packingBreakdownMeta === 'object' ? safeData.packingBreakdownMeta : {};
    const attrMeta = safeData.attributeBreakdownMeta && typeof safeData.attributeBreakdownMeta === 'object' ? safeData.attributeBreakdownMeta : {};
    const packingEntries = recordEntries(safeData.packingBreakdown);
    const packingNodes = packingEntries.length > 0
        ? packingEntries.map(([pType, pCost], idx) => {
            const hint = formatPackingHint(packingMeta[pType]);
            if (asTableRows) {
                return (
                    <tr key={`pack-${idx}`}>
                        <ChargeLabel label={pType} hint={hint} from="Packing rate table for this packing type (rate per CBM or per m², and the minimum)." />
                        <td>{typeof pCost === 'number' ? money(pCost, 2) : money(parseFloat(pCost), 2)}</td>
                    </tr>
                );
            }
            return (
                <div key={`pack-${idx}`} className="border-bottom py-1" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between">
                        <span className="text-secondary">
                            <ExplainToggle label={pType} why={hint} from="Packing rate table for this packing type (rate per CBM or per m², and the minimum)." />
                        </span>
                        <span className="font-weight-bold">{typeof pCost === 'number' ? money(pCost, 2) : money(parseFloat(pCost), 2)}</span>
                    </div>
                </div>
            );
        })
        : [asTableRows ? (
            <tr key="pack-empty"><td className="text-muted">Packing</td><td>0.00 <span className="text-muted font-weight-normal" style={{ fontSize: '0.75rem' }}>(If Requested)</span></td></tr>
        ) : (
            <div key="pack-empty" className="d-flex justify-content-between border-bottom py-1" style={{ fontSize: '13px' }}>
                <span className="text-secondary">Packing</span>
                <span className="font-weight-bold">0.00 (If Requested)</span>
            </div>
        )];

    if (freightDiff > 0) {
        const originalRound = money(freightPair?.original, 2);
        const packedRound = money(freightPair?.packed, 2);
        const pairNote = `Original round off ${originalRound} · Packed round off ${packedRound}`;
        const pairHint = `Packed round off ${packedRound} − original round off ${originalRound}. Packing and attribute charges are not inside this difference.`;
        const diffRow = asTableRows ? (
            <tr key="pack-freight-diff">
                <td className="text-muted" style={{ verticalAlign: 'top' }}>
                    <ExplainToggle label="Extra freight (packing size)" why={pairHint} from="The quote is run again on the packed size. The difference is taken after profit, at round off. The freight line stays on the original size." />
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{pairNote}</div>
                </td>
                <td>{money(freightDiff, 2)}</td>
            </tr>
        ) : (
            <div key="pack-freight-diff" className="border-bottom py-1" style={{ fontSize: '13px' }}>
                <div className="d-flex justify-content-between">
                    <span className="text-secondary">
                        <ExplainToggle label="Extra freight (packing size)" why={pairHint} from="Taken after profit, at round off. Packing and attribute charges are not inside this difference." />
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>{pairNote}</div>
                    </span>
                    <span className="font-weight-bold">{money(freightDiff, 2)}</span>
                </div>
            </div>
        );
        packingNodes.push(diffRow);
    }

    const attrEntries = recordEntries(safeData.attributeBreakdown);
    const attrNodes = attrEntries.length > 0
        ? attrEntries.map(([attrName, attrCost], idx) => {
            const hint = formatAttrHint(attrMeta[attrName]);
            if (asTableRows) {
                return (
                    <tr key={`attr-${idx}`}>
                        <ChargeLabel label={attrName} hint={hint} from="Attribute charge table for this shipment mode." />
                        <td>{money(parseFloat(attrCost), 2)}</td>
                    </tr>
                );
            }
            return (
                <div key={`attr-${idx}`} className="border-bottom py-1" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between">
                        <span className="text-secondary">
                            <ExplainToggle label={attrName} why={hint} from="Attribute charge table for this shipment mode." />
                        </span>
                        <span className="font-weight-bold">{money(parseFloat(attrCost), 2)}</span>
                    </div>
                </div>
            );
        })
        : [asTableRows ? (
            <tr key="attr-empty"><td className="text-muted">Attribute Fees</td><td>0.00 <span className="text-muted font-weight-normal" style={{ fontSize: '0.75rem' }}>(If Applicable)</span></td></tr>
        ) : (
            <div key="attr-empty" className="d-flex justify-content-between border-bottom py-1" style={{ fontSize: '13px' }}>
                <span className="text-secondary">Attribute Fees</span>
                <span className="font-weight-bold">0.00 (If Applicable)</span>
            </div>
        )];

    return [...packingNodes, ...attrNodes];
};

/** Build a readable label + hint for a numeric charge key */
const enrichChargeLabel = (key, value, ctx = {}) => {
    const baseLabel = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const { data, details, freightAmt, courierPrefix } = ctx;
    const lower = key.toLowerCase();
    const rateBasis = details?.rate_basis;
    const chWt = parseFloat(rateBasis?.chargeable_weight);
    const fmtAed = (n) => money(Number(n), 2);

    // Air: fuel and freight are per-kg rates × chargeable weight, not a % of freight
    if (lower === 'fuel_amount' && chWt > 0 && parseFloat(rateBasis.fuel_rate_per_kg) > 0) {
        const perKg = parseFloat(rateBasis.fuel_rate_per_kg);
        return {
            label: `${baseLabel} (AED ${fmtAed(perKg)}/kg)`,
            hint: `${chWt} kg × AED ${fmtAed(perKg)}/kg`,
            from: 'Air freight tariff fuel rate per kg, multiplied by the rounded chargeable weight.',
        };
    }
    if (lower === 'fregiht_amount' && chWt > 0 && parseFloat(rateBasis.freight_rate_per_kg) > 0) {
        const perKg = parseFloat(rateBasis.freight_rate_per_kg);
        const label = baseLabel.replace('Fregiht', 'Freight');
        if (rateBasis.freight_minimum_applied) {
            return {
                label,
                hint: `Minimum AED ${fmtAed(rateBasis.freight_minimum)} applied (${chWt} kg × AED ${fmtAed(perKg)}/kg = ${fmtAed(chWt * perKg)})`,
                from: 'Air freight rate table. The per-kg amount was below the minimum on that rate row, so the minimum was used.',
            };
        }
        return {
            label,
            hint: `${chWt} kg × AED ${fmtAed(perKg)}/kg`,
            from: 'Air freight rate table: rate per kg for this route × rounded chargeable weight.',
        };
    }

    if (lower.includes('fuel')) {
        let pct = getFuelPercentFromData(details, data, courierPrefix);
        if (pct == null) pct = inferFuelPercent(value, freightAmt);
        const surchargeAmt = Number(ctx.surchargeAmt) || 0;
        const fuelBase = (Number(freightAmt) || 0) + surchargeAmt;
        if (pct != null) {
            const courierFuel = ['fedex', 'ups', 'dhl'].includes(String(courierPrefix || '').toLowerCase());
            return {
                label: `${baseLabel} (${pct}%)`,
                hint: courierFuel && surchargeAmt > 0
                    ? `${pct}% × (Freight ${money(Number(freightAmt), 2)} + Surcharges ${money(surchargeAmt, 2)})`
                    : freightAmt > 0
                        ? `${pct}% × Freight ${money(Number(freightAmt), 2)}`
                        : `Fuel surcharge ${pct}%`,
                from: courierFuel
                    ? 'Fuel percentage on the courier additional-charges row. Surcharges are added to the freight first, then fuel is calculated on that total.'
                    : 'Fuel percentage on the carrier or mode additional-charges row, applied to the freight amount.',
            };
        }
    }
    if (['fedex', 'ups', 'dhl'].includes(String(courierPrefix || '').toLowerCase())) {
        const surchargeLabels = {
            ups_additional_amount: 'Additional handling surcharge',
            additional_surcharge_amount: 'Additional handling surcharge',
            additional_shipment_amount: 'Express weight charge',
            ups_large_package_surcharge_amount: 'Large package surcharge',
            large_package_surcharge_amount: 'Large package surcharge',
            ups_over_max_surcharge_amount: 'Over max limit surcharge',
            over_max_surcharge_amount: 'Over max limit surcharge',
            peak_demand_surcharge: 'Peak / demand surcharge',
        };
        if (surchargeLabels[lower]) {
            return {
                label: surchargeLabels[lower],
                hint: 'Applied from the carrier conditions on Courier Rates.',
                from: 'Courier Rates. A package is charged when one of that condition’s limits is crossed.',
            };
        }
    }
    if (lower.includes('govt_tax') || lower.includes('govt tax') || lower.includes('govt')) {
        const fuelLine = parseFloat(details?.[`${String(courierPrefix || '').toLowerCase()}_fuel_charges`] || details?.fuel_charges || 0) || 0;
        const surchargeAmt = Number(ctx.surchargeAmt) || 0;
        const govtBase = (Number(freightAmt) || 0) + surchargeAmt + fuelLine;
        const courierFuel = ['fedex', 'ups', 'dhl'].includes(String(courierPrefix || '').toLowerCase());
        const taxAmount = Number(value) || 0;
        return {
            label: baseLabel,
            hint: courierFuel && taxAmount <= 0
                ? 'Calculated after fuel. No tax on this shipment because the weight is above the Government tax limit on Courier Rates.'
                : courierFuel && govtBase > 0
                    ? `After fuel. Freight ${money(Number(freightAmt), 2)} + Surcharges ${money(surchargeAmt, 2)} + Fuel ${money(fuelLine, 2)}`
                    : 'Govt tax on freight + fuel (tariff %)',
            from: 'Government tax on Courier Rates. It is calculated after fuel, on freight plus the surcharges plus fuel, and only while the shipment weight stays within the Up to limit.',
        };
    }
    if (lower === 'airline_handling') {
        return {
            label: baseLabel,
            hint: 'Origin airline handling (tariff)',
            from: 'Origin air local charges: airline handling on the air tariff.',
        };
    }
    if (lower.includes('fregiht') || lower === 'freight' || lower.includes('freight')) {
        const explained = explainFreight(key, parseFloat(value), ctx);
        if (explained) return explained;
        return { label: baseLabel.replace('Fregiht', 'Freight'), hint: null };
    }
    if (lower.includes('nvocc')) {
        return {
            label: baseLabel,
            hint: 'Destination NVOCC tariff',
            from: 'Destination sea door charges (NVOCC) for this country.',
        };
    }
    if (lower.includes('handling')) {
        return {
            label: baseLabel,
            hint: 'Origin handling (min applied if below minimum)',
            from: 'Origin local handling tariff. If the calculated amount is under the minimum, the minimum is used.',
        };
    }
    if (lower === 'destination_inland_amount' || lower === 'inland') {
        const tariff = data?.local_tariffs || {};
        const priceRow = Array.isArray(tariff.local_tariff_price) ? tariff.local_tariff_price[0] : tariff.local_tariff_price;
        const km = Number(data?.near_city_door?.destinationResult?.distance);
        const cbmBand = Number(tariff.local_tariff_volume);
        const shipmentCbm = Number(data?.totalVol);
        const fromCity = data?.near_city_door?.destinationResult?.city_name || '';
        const toPlace = ctx.destinationPlace || 'the destination address';
        const kmText = Number.isFinite(km) && km > 0 ? `${money(km, 2)} km` : '';
        const cbmText = Number.isFinite(cbmBand) ? `CBM band ${money(cbmBand, 3)}` : '';
        const minKm = Number(priceRow?.min_distance);
        const maxKm = Number(priceRow?.max_distance);
        const range = Number.isFinite(minKm) && Number.isFinite(maxKm) ? `${money(minKm, 2)}–${money(maxKm, 2)} km` : '';
        const shown = [kmText, cbmText].filter(Boolean).join(', ');
        const fromPoint = fromCity ? `${fromCity} (door clearance city)` : 'the door clearance city';
        const kmHow = ctx.shipmentMode === 'AIR'
            ? 'Distance is measured from that clearance city to the destination.'
            : 'Kilometres are the driving distance from that clearance city to the destination.';
        return {
            label: shown ? `Destination Inland (${shown})` : 'Destination Inland',
            hint: [
                `From ${fromPoint} to ${toPlace}.`,
                kmText ? `${kmHow} Result: ${kmText}.` : '',
                range ? `The local tariff row ${range} is used because ${kmText || 'that distance'} falls inside it.` : '',
                Number.isFinite(shipmentCbm) ? `Shipment CBM is ${money(shipmentCbm, 3)}.` : '',
                Number.isFinite(cbmBand) ? `The CBM band is the first tariff volume at or above that shipment CBM, which is ${money(cbmBand, 3)}.` : '',
                'The inland amount is the flat price on that km row. It is not kilometres times a rate.',
            ].filter(Boolean).join(' '),
            from: 'Destination local tariff for this country. The start point is the door clearance city chosen for this destination. The end point is the destination address. The CBM band is chosen from the shipment CBM.',
        };
    }
    return { label: baseLabel, hint: null };
};

const calculateMarketplaceCharges = (baseFreight, totalGoodsValue, chargeableWeight, insuranceCost, products, marketplaceSettings) => {
    const settings = marketplaceSettings || {};

    const {
        over_weight_max = 0,
        over_weight_charge = 0,
        caf = 0,
        duty_tax_additional_charge,
        duty_tax_freight_per,
        duty_tax_insurance_per,
        local_vat_per = 0,
        min_duty_tax
    } = settings;

    let overweightFee = 0;
    if (parseFloat(over_weight_max) > 0 && chargeableWeight > parseFloat(over_weight_max)) {
        overweightFee = parseFloat(over_weight_charge) || 0;
    }

    const cafFee = 0; // CAF is not applied to freight, only duty/tax
    const finalFreight = baseFreight + overweightFee;

    let totalDutyVal = 0;
    let totalVatVal = 0;
    let totalFreightForCaf = 0;

    const freightPercent = (duty_tax_freight_per !== undefined && duty_tax_freight_per !== null && duty_tax_freight_per !== '') ? parseFloat(duty_tax_freight_per) : 25;
    const insurancePercent = (duty_tax_insurance_per !== undefined && duty_tax_insurance_per !== null && duty_tax_insurance_per !== '') ? parseFloat(duty_tax_insurance_per) : 1;
    const additionalCharge = (duty_tax_additional_charge !== undefined && duty_tax_additional_charge !== null && duty_tax_additional_charge !== '') ? parseFloat(duty_tax_additional_charge) : 50;
    const minDuty = (min_duty_tax !== undefined && min_duty_tax !== null && min_duty_tax !== '') ? parseFloat(min_duty_tax) : 100;

    if (Array.isArray(products) && products.length > 0) {
        products.forEach(prod => {
            const dutyRate = parseFloat(prod.dutyRate || prod.duty || prod.hs_duty) || 0;
            const vatRate = parseFloat(prod.vatRate || prod.vat || prod.hs_vat) || 0;

            const itemVal = parseFloat(prod.price || prod.totalValue) || 0;
            const itemFreight = itemVal * (freightPercent / 100);
            const itemInsurance = itemVal * (insurancePercent / 100);

            const itemCIF = itemVal + itemFreight + itemInsurance;

            const itemDuty = itemCIF * (dutyRate / 100);
            const itemVat = (itemCIF + itemDuty) * (vatRate / 100);

            totalDutyVal += itemDuty;
            totalVatVal += itemVat;
            totalFreightForCaf += itemFreight;
        });
    }

    const dutyCafAmount = (totalDutyVal + totalVatVal) * ((parseFloat(caf) || 0) / 100);
    let totalDutyAndTaxes = totalDutyVal + totalVatVal + dutyCafAmount;

    if (totalGoodsValue > 0 && (totalDutyVal > 0 || totalVatVal > 0)) {
        totalDutyAndTaxes += additionalCharge;
    }

    if (minDuty > 0 && totalDutyAndTaxes > 0 && totalDutyAndTaxes < minDuty) {
        totalDutyAndTaxes = minDuty;
    }

    return {
        overweightFee,
        cafFee,
        finalFreight,
        totalDutyEst: totalDutyAndTaxes,
        dutyVal: totalDutyVal,
        vatVal: totalVatVal,
        cafPercent: parseFloat(caf) || 0,
        dutyCafAmount: dutyCafAmount
    };
};

const quoteText = (doc) => [
    'World2Door quotation',
    doc.title,
    `${doc.from} to ${doc.to}`,
    doc.date,
    ...doc.lines.map((line) => `${line.label}: AED ${line.value}`),
    `Freight total: AED ${doc.freight}`,
    `Duty and taxes: ${doc.duty === 'At actuals' ? doc.duty : `AED ${doc.duty}`}`,
].join('\n');

const shareQuote = (doc) => {
    const text = quoteText(doc);
    const page = window.open('', '_blank', 'noopener,noreferrer,width=820,height=900');
    if (!page) return;
    const rows = doc.lines.map((line) => `<tr><td>${line.label}</td><td style="text-align:right">AED ${line.value}</td></tr>`).join('');
    page.document.write(`<!DOCTYPE html><html><head><title>Quotation ${doc.title}</title>
      <style>
        body{font-family:Arial,sans-serif;color:#0f172a;margin:32px}
        h1{font-size:22px;margin:0} .muted{color:#64748b;font-size:13px}
        table{width:100%;border-collapse:collapse;margin-top:18px}
        td{padding:8px 0;border-bottom:1px solid #e2e8f0;font-size:14px}
        .total{margin-top:16px;background:#f0fdf4;padding:12px 14px;border-radius:8px;font-weight:700}
      </style></head><body>
      <h1>World2Door</h1>
      <p class="muted">Freight quotation · ${doc.date}</p>
      <h2 style="font-size:18px;margin-bottom:4px">${doc.title}</h2>
      <p class="muted">${doc.mode} · ${doc.from} → ${doc.to}</p>
      <table>${rows}</table>
      <div class="total">Freight total AED ${doc.freight}<br/>Duty and taxes ${doc.duty === 'At actuals' ? 'at actuals' : `AED ${doc.duty}`}</div>
      <script>window.onload=function(){window.print()}<\/script>
      </body></html>`);
    page.document.close();
    if (navigator.share) navigator.share({ title: `Quotation ${doc.title}`, text }).catch(() => {});
};

export const RateChecker = () => {
    const [locations, setLocations] = useState({
        from: '',
        to: ''
    });
    const [destinationPostal, setDestinationPostal] = useState('');
    const [modeChargeRules, setModeChargeRules] = useState({ courier: emptyModeRules(), land: emptyModeRules() });

    const [products, setProducts] = useState([{
        name: '',
        hsCode: '',
        hsStatus: 'idle',
        hsValidationMsg: '',
        quantity: 1,
        totalValue: '',
        coo: 'United Arab Emirates',
        perfume: false,
        battery: false,
        cosmetic: false,
        magnet: false,
        msdsType: 'link',
        msdsLink: '',
        msdsFile: null,
        packages: [{ count: 1, weight: '', length: '', width: '', height: '', packaging_type: 'Packaging not required' }]
    }]);

    const [countries, setCountries] = useState([]);
    const [packingRates, setPackingRates] = useState([]);
    const [attributeCharges, setAttributeCharges] = useState([]);
    const [insuranceRate, setInsuranceRate] = useState(2);
    const [insuranceMin, setInsuranceMin] = useState(175);
    const [loading, setLoading] = useState(null); // 'AIR', 'SEA', etc.
    const [results, setResults] = useState({});
    const [activeTab, setActiveTab] = useState(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [systemSettings, setSystemSettings] = useState(null);
    const [allProfitMargins, setAllProfitMargins] = useState([]);
    // selectedTierIndex: 0 = Tier 1 (100 shipments), 1 = Tier 2 (50 shipments), 2 = Tier 3 (0 shipments)
    const [selectedTierIndex, setSelectedTierIndex] = useState(0);
    const [quoteDoc, setQuoteDoc] = useState(null);

    const TIERS = [
        { label: "Tier 1 ( 100 above shipment per month / 100000 above revenue per month )", min_shipment: 100 },
        { label: "Tier 2 ( 50 to 100 shipment per month / 50000 to 100000 revenue per month )", min_shipment: 50 },
        { label: "Tier 3 ( 0 to 50 shipment per month / 0 to 50000 revenue per month )", min_shipment: 0 }
    ];
    const location = useLocation();
    const initializedFromQuote = useRef(false);

    let modeFilter = 'Air_Freight';
    if (activeTab === 'SEA') modeFilter = 'Sea_Freight';
    else if (activeTab === 'COURIER') modeFilter = 'Courier';
    else if (activeTab === 'LAND') modeFilter = 'Land_Freight';
    else if (activeTab === 'AIR') modeFilter = 'Air_Freight';

    let profitMargins = allProfitMargins;

    useEffect(() => {
        if (location.state?.prefillQuote && !initializedFromQuote.current) {
            initializedFromQuote.current = true;
            const quote = location.state.prefillQuote;

            const parseLoc = (locStr) => {
                if (!locStr) return { city: '', country: '' };
                const parts = locStr.split(', ');
                if (parts.length > 1) {
                    return { city: parts[0], country: parts[1] };
                }
                return { city: '', country: parts[0] };
            };

            const fromObj = {
                city: quote.packing?.origin_city || parseLoc(quote.packing?.origin).city,
                country: quote.packing?.origin_country || parseLoc(quote.packing?.origin).country,
                country_short_name: quote.packing?.origin_country_short_name || '',
                latitude: quote.packing?.origin_latitude || '',
                longitude: quote.packing?.origin_longitude || ''
            };

            const toObj = {
                city: quote.packing?.destination_city || parseLoc(quote.packing?.destination).city,
                country: quote.packing?.destination_country || parseLoc(quote.packing?.destination).country,
                country_short_name: quote.packing?.destination_country_short_name || '',
                latitude: quote.packing?.destination_latitude || '',
                longitude: quote.packing?.destination_longitude || ''
            };

            setLocations({
                from: fromObj,
                to: toObj
            });

            if (quote.packing?.items && quote.packing.items.length > 0) {
                const mappedProducts = quote.packing.items.map(item => ({
                    name: item.item_description || '',
                    hsCode: item.hs_code || '',
                    hsStatus: 'idle',
                    hsValidationMsg: '',
                    quantity: 1,
                    totalValue: item.price || '',
                    coo: item.coo || 'United Arab Emirates',
                    perfume: item.is_perfume || false,
                    battery: item.contains_battery || item.is_battery || false,
                    cosmetic: item.is_cosmetic || item.is_cosmetics || false,
                    magnet: item.contains_magnet || item.is_magnet || false,
                    msdsType: 'link',
                    msdsLink: '',
                    msdsFile: null,
                    packages: [{
                        count: item.no_of_pkgs || 1,
                        weight: item.dimension?.kilogram || item.total_weight_KG || '',
                        length: item.dimension?.length || '',
                        width: item.dimension?.width || '',
                        height: item.dimension?.height || '',
                        packaging_type: item.packaging_type || 'Packaging not required'
                    }]
                }));
                setProducts(mappedProducts);
            }

            // Auto-trigger rate checking based on saved mode or default to Courier
            setTimeout(() => {
                const mode = quote.selected_courier_type || 'COURIER';
                let btnId = 'btn-fetch-COURIER'; // default
                if (mode === 'AIR') btnId = 'btn-fetch-AIR';
                if (mode.startsWith('PORT')) btnId = 'btn-fetch-SEA'; // Assuming Port to Port is Sea
                if (mode.startsWith('DOOR') && mode.includes('SEA')) btnId = 'btn-fetch-SEA';
                if (mode.startsWith('DOOR') && mode.includes('COURIER')) btnId = 'btn-fetch-COURIER';

                const modeBtn = document.getElementById(btnId);
                if (modeBtn) modeBtn.click();
            }, 1000);
        }
    }, [location.state]);

    useEffect(() => {
        DataService.get('/admin/system_settings')
            .then(res => {
                const settingsData = res?.data?.data || res?.data;
                if (settingsData) setSystemSettings(settingsData);
            })
            .catch(err => console.error("Error fetching system settings", err));

        DataService.get('/admin/get_country')
            .then(res => {
                const countryData = res?.data?.data || res?.data || res || [];
                setCountries(Array.isArray(countryData) ? countryData : []);
            })
            .catch(err => console.error("Error fetching countries", err));

        DataService.get('/admin/get_packing')
            .then(res => {
                const pData = res?.data?.data?.packings || res?.data?.packings || [];
                setPackingRates(pData);
            })
            .catch(err => console.error("Error fetching packing rates", err));

        DataService.get('/admin/attribute_additional_charges')
            .then(res => {
                if (res?.data) {
                    setAttributeCharges(res.data);
                }
            })
            .catch(err => console.error("Error fetching attribute charges", err));

        DataService.get('/admin/get_insurance')
            .then(res => {
                const insData = res?.data?.data || res?.data || [];
                if (insData && insData.length > 0 && insData[0].insurance_premium) {
                    setInsuranceRate(Number(insData[0].insurance_premium));
                    setInsuranceMin(Number(insData[0].minimum_aed) || 0);
                }
            })
            .catch(err => console.error("Error fetching insurance", err));

        DataService.get('/admin/get_profit_margin_tarrif', { limit: 100 })
            .then(res => {
                const margins = res?.data?.data || res?.data || [];
                setAllProfitMargins(margins);
            })
            .catch(err => console.error("Error fetching profit margins", err));

        const loadModeRules = (mode) => DataService.get('/check/mode_charges', { mode }).then((res) => {
            const grouped = Array.isArray(res?.data?.reimbursement) ? res.data : res?.data?.data;
            return grouped && Array.isArray(grouped.reimbursement) ? grouped : emptyModeRules();
        }).catch(() => emptyModeRules());
        Promise.all([loadModeRules('courier'), loadModeRules('land')]).then(([courier, land]) => {
            setModeChargeRules({ courier, land });
        });
    }, []);

    // Reset results when core inputs change to force recalculation
    useEffect(() => {
        if (Object.keys(results).length > 0 || isSubmitted) {
            setResults({});
            setIsSubmitted(false);
            setActiveTab(null);
        }
    }, [locations.from, locations.to, products]);

    const handleLocationChange = (field, value) => {
        setLocations(prev => ({ ...prev, [field]: value }));
        if (field === 'to') {
            setDestinationPostal(value && value.postal_code ? String(value.postal_code) : '');
        }
    };

    const handleProductChange = (pIdx, field, value) => {
        const newProducts = [...products];
        newProducts[pIdx][field] = value;
        setProducts(newProducts);
    };

    const handlePackageChange = (pIdx, pkgIdx, field, value) => {
        const newProducts = [...products];
        newProducts[pIdx].packages[pkgIdx][field] = value;
        setProducts(newProducts);
    };

    const addProduct = () => {
        setProducts([...products, {
            name: '', hsCode: '', hsStatus: 'idle', hsValidationMsg: '',
            quantity: 1, totalValue: '', coo: 'United Arab Emirates',
            perfume: false, battery: false, cosmetic: false, magnet: false,
            msdsType: 'link', msdsLink: '', msdsFile: null,
            packages: [{ count: 1, weight: '', length: '', width: '', height: '', packaging_type: 'Packaging not required' }]
        }]);
    };

    const addPackage = (pIdx) => {
        const newProducts = [...products];
        newProducts[pIdx].packages.push({ count: 1, weight: '', length: '', width: '', height: '', packaging_type: 'Packaging not required' });
        setProducts(newProducts);
    };

    const removePackage = (pIdx, pkgIdx) => {
        const newProducts = [...products];
        if (newProducts[pIdx].packages.length > 1) {
            newProducts[pIdx].packages.splice(pkgIdx, 1);
            setProducts(newProducts);
        }
    };

    const removeProduct = (pIdx) => {
        if (products.length > 1) {
            const newProducts = [...products];
            newProducts.splice(pIdx, 1);
            setProducts(newProducts);
        }
    };

    const validateHsCode = async (pIdx) => {
        const prod = products[pIdx];
        if (!prod.hsCode || prod.hsCode.length < 4) return;

        const newProducts = [...products];
        newProducts[pIdx].hsStatus = 'loading';
        setProducts(newProducts);

        try {
            const toData = locations.to || {};
            const res = await DataService.post('/retailer/hscode/validate-multiple', {
                hs_codes: [prod.hsCode],
                destination_country: toData.country || '',
                destination_country_short_name: toData.country_short_name || ''
            });

            const np = [...products];
            const validArray = res?.valid || res?.data?.valid || res?.data?.data?.valid || [];
            if (validArray.length > 0) {
                np[pIdx].hsStatus = 'valid';
                np[pIdx].hsValidationMsg = validArray[0].description || 'Valid HS Code';
                np[pIdx].dutyRate = validArray[0].duty !== undefined ? validArray[0].duty : 0;
                np[pIdx].vatRate = validArray[0].vat !== undefined ? validArray[0].vat : 0;
            } else {
                np[pIdx].hsStatus = 'invalid';
                np[pIdx].hsValidationMsg = 'Invalid HS Code';
            }
            setProducts(np);
        } catch (error) {
            const np = [...products];
            np[pIdx].hsStatus = 'invalid';
            np[pIdx].hsValidationMsg = 'Validation failed';
            setProducts(np);
        }
    };

    const fetchRates = async (mode) => {
        setLoading(mode);

        const fromData = locations.from || {};
        const toData = locations.to || {};

        let totalQty = 0;
        let totalVol = 0;
        let totalWt = 0;
        let packedVol = 0;
        let packedWt = 0;
        let hasPacking = false;
        let totalGoodsValue = 0;
        let totalPackingCost = 0;
        let packingBreakdown = {};
        let packingBreakdownMeta = {};

        const mappedItems = [];
        const packedItems = [];

        products.forEach((prod, pIdx) => {
            const prodQty = parseInt(prod.quantity) || 1;
            totalGoodsValue += parseFloat(prod.totalValue) || 0;

            (prod.packages || []).forEach((pkg, idx) => {
                const qty = (parseInt(pkg.count) || 1) * prodQty;
                const wt = parseFloat(pkg.weight) || 0;
                const l = parseFloat(pkg.length) || 0;
                const w = parseFloat(pkg.width) || 0;
                const h = parseFloat(pkg.height) || 0;

                const kind = packingKind(pkg.packaging_type);
                const rateObj = kind && packingRates && packingRates.length > 0
                    ? packingRates.find(r => packingKind(r.packing_type) === kind)
                    : null;
                let shipL = l;
                let shipW = w;
                let shipH = h;
                let shipWt = wt;
                if (kind) {
                    hasPacking = true;
                    const grown = grownPackage(l, w, h, wt, kind);
                    shipL = grown.l;
                    shipW = grown.w;
                    shipH = grown.h;
                    shipWt = grown.wt;
                    if (rateObj) {
                        const rate = parseFloat(rateObj.rate_per_cmb || 0);
                        const min = parseFloat(rateObj.minimum || 0);
                        const method = kind === 'carton' ? 'cbm' : 'area';
                        const cbm = (l * w * h) / 1000000;
                        const area = sixSideSqm(l, w, h);
                        const unit = kind === 'carton' ? cbm : area;
                        const pCost = Math.max(min, unit * rate) * qty;
                        totalPackingCost += pCost;
                        const displayName = pkg.packaging_type;
                        packingBreakdown[displayName] = (packingBreakdown[displayName] || 0) + pCost;
                        if (!packingBreakdownMeta[displayName]) {
                            packingBreakdownMeta[displayName] = { method, rate, min, lines: [] };
                        }
                        packingBreakdownMeta[displayName].lines.push({
                            method,
                            rate,
                            min,
                            cbm,
                            area,
                            qty,
                            weight: wt,
                            dims: `${l}×${w}×${h}`,
                        });
                    }
                }

                totalQty += qty;
                totalWt += (wt * qty);
                const vol = (l * w * h / 1000000) * qty;
                totalVol += vol;
                packedWt += shipWt * qty;
                const packedPackageVol = (shipL * shipW * shipH / 1000000) * qty;
                packedVol += packedPackageVol;

                const itemBase = {
                    item_description: prod.name || 'Product',
                    no_of_pkgs: qty,
                    dimension: {
                        kilogram: wt,
                        length: l,
                        width: w,
                        height: h
                    },
                    total_volume_CBM: vol,
                    total_weight_KG: wt * qty,
                    item_token: `item_mock_${Date.now()}_${pIdx}_${idx}`,
                    hs_code: prod.hsCode,
                    // Total Value is the product line total. Put it on the first package only
                    // so duty is not multiplied once per package.
                    price: idx === 0 ? (parseFloat(prod.totalValue) || 0) : 0,
                    quantity: 1,
                    coo: prod.coo,
                    is_perfume: prod.perfume || false,
                    contains_battery: prod.battery || false,
                    is_cosmetic: prod.cosmetic || false,
                    contains_magnet: prod.magnet || false,
                    packaging_type: pkg.packaging_type || 'Packaging not required',
                    dutyRate: prod.dutyRate || prod.duty || 0,
                    vatRate: prod.vatRate || prod.vat || 0,
                    duty: prod.dutyRate || prod.duty || 0,
                    vat: prod.vatRate || prod.vat || 0
                };
                mappedItems.push(itemBase);
                packedItems.push({
                    ...itemBase,
                    dimension: {
                        kilogram: shipWt,
                        length: shipL,
                        width: shipW,
                        height: shipH
                    },
                    total_volume_CBM: packedPackageVol,
                    total_weight_KG: shipWt * qty,
                    item_token: `${itemBase.item_token}_packed`
                });
            });
        });

        const finalPayload = {
            origin: fromData.city || '',
            origin_country: fromData.country || '',
            destination: toData.city || '',
            destination_country: toData.country || '',
            origin_country_short_name: fromData.country_short_name || 'AE',
            origin_city: fromData.city || '',
            origin_latitude: fromData.latitude || 25.2048,
            origin_longitude: fromData.longitude || 55.2708,
            destination_country_short_name: toData.country_short_name || 'IN',
            destination_city: toData.city || '',
            destination_latitude: toData.latitude || 28.6139,
            destination_longitude: toData.longitude || 77.2090,
            number_of_packages: totalQty,
            volume: totalVol,
            weight: totalWt,
            goods_value: totalGoodsValue,
            items_goods: totalGoodsValue,
            quote_token: `quote_mock_${Date.now()}`,
            deliverer: 'RETAILER',
            items: mappedItems
        };

        let endpoint = '';
        let modeStr = '';
        if (mode === 'AIR') { endpoint = '/check/get_air_quotes'; modeStr = 'Air'; }
        if (mode === 'SEA') { endpoint = '/check/get_sea_quotes'; modeStr = 'Sea'; }
        if (mode === 'COURIER') { endpoint = '/check/get_courier_quote'; modeStr = 'Courier'; }
        if (mode === 'LAND') { endpoint = '/check/get_land_quote'; modeStr = 'Land'; }

        if (selectedTierIndex === 1) finalPayload.tier_name = 'Tier_2';
        else if (selectedTierIndex === 2) finalPayload.tier_name = 'Tier_3';
        else finalPayload.tier_name = 'Tier_1';

        try {
            const hsCodes = products.map(p => p.hsCode).filter(Boolean);
            const destCountry = toData.country || '';
            let discountRule = null;
            try {
                const dRes = await DataService.post('/retailer/evaluate_discount', {
                    total_weight: totalWt,
                    total_cbm: totalVol,
                    hs_codes: hsCodes,
                    destination_country: destCountry,
                    promo_code: '',
                    mode: modeStr
                });
                const dData = dRes?.data || dRes;
                if (dData?.discount || dData?.data?.discount) discountRule = dData?.discount || dData?.data?.discount;
            } catch (e) { console.error('Error evaluating discount', e); }

            let computedDuty = 0;
            let computedInsurance = 0;
            let dutyApiData = null;
            try {
                const dutyRes = await DataService.post('/retailer/calculate-duty', {
                    items: products.map(p => ({
                        hs_code: p.hsCode || null,
                        // Total Value is already the line total (same as retailer goods value). Do not multiply by quantity.
                        price: p.totalValue || 0,
                        quantity: 1
                    })),
                    destination_country: toData.country || '',
                    destination_country_short_name: toData.country_short_name || ''
                });
                const dData = dutyRes?.data || dutyRes;
                if (dData?.status) {
                    dutyApiData = dData;
                    computedDuty = parseFloat(dData.duty_estimate || 0);
                    computedInsurance = parseFloat(dData.insurance_cost || 0);
                    if (dData.items && Array.isArray(dData.items)) {
                        mappedItems.forEach(mi => {
                            const matched = dData.items.find(i => String(i.hs_code) === String(mi.hs_code));
                            const prodMatch = products.find(p => String(p.hsCode) === String(mi.hs_code));
                            const apiDuty = matched ? parseFloat(matched.duty_rate || 0) : 0;
                            const apiVat = matched ? parseFloat(matched.vat_rate || 0) : 0;
                            const prodDuty = parseFloat(prodMatch?.dutyRate || prodMatch?.duty || 0) || 0;
                            const prodVat = parseFloat(prodMatch?.vatRate || prodMatch?.vat || 0) || 0;
                            mi.dutyRate = apiDuty > 0 ? apiDuty : prodDuty;
                            mi.vatRate = apiVat > 0 ? apiVat : prodVat;
                            mi.duty = mi.dutyRate;
                            mi.vat = mi.vatRate;
                        });
                    }
                }
            } catch (e) { console.error('Error computing duty and insurance', e); }

            // Attribute fees calculated after quote so we use API margin % (weight/volume) dynamically
            const shipmentHasPerfume = products.some(p => p.perfume);
            const shipmentHasBattery = products.some(p => p.battery);
            const shipmentHasCosmetic = products.some(p => p.cosmetic);
            const shipmentHasMagnet = products.some(p => p.magnet);

            finalPayload.items = JSON.parse(JSON.stringify(mappedItems));
            const packedPayload = {
                ...finalPayload,
                items: JSON.parse(JSON.stringify(packedItems)),
                weight: packedWt,
                volume: packedVol
            };
            const response = await DataService.post(endpoint, finalPayload);
            const packedResponse = hasPacking ? await DataService.post(endpoint, packedPayload) : null;
            const responseData = response.data || {};
            const allData = response.all_data || {};
            const originalQuote = { ...allData, ...responseData };
            const packedQuote = packedResponse ? { ...(packedResponse.all_data || {}), ...(packedResponse.data || {}) } : {};
            const serviceBlocks = (root) => {
                const blocks = {};
                ['port_delivery', 'door_delivery', 'consolidation_delivery'].forEach((key) => {
                    if (root[key]) blocks[key] = root[key];
                });
                Object.keys(root).forEach((key) => {
                    if (key.endsWith('_courier_rates') && Array.isArray(root[key])) {
                        root[key].forEach((rate, index) => {
                            blocks[`${key}:${index}`] = rate;
                        });
                    }
                });
                return blocks;
            };
            const oldBlocks = serviceBlocks(originalQuote);
            const newBlocks = serviceBlocks(packedQuote);
            const freightDelta = {};
            const freightCompare = {};
            Object.keys(oldBlocks).forEach((key) => {
                const oldRound = roundOffThroughMargin(oldBlocks[key], mode, key, totalGoodsValue, profitMargins, selectedTierIndex);
                const newRound = roundOffThroughMargin(newBlocks[key], mode, key, totalGoodsValue, profitMargins, selectedTierIndex);
                const diff = newRound - oldRound;
                if (diff > 0) {
                    freightDelta[key] = diff;
                    freightCompare[key] = { original: oldRound, packed: newRound };
                }
            });

            // Dynamic M/E from quote margin tariff (not hardcoded)
            const marginCfg = allData.margin || {};
            const weightPct = parseFloat(marginCfg.weight_percentage);
            const volumePct = parseFloat(marginCfg.volume_percentage);
            // Courier/Air/Land weight M/E = 2%; Sea = 3%. Volume: Air/Land/Courier = 3%, Sea = 10%
            const defaultWeightPct = mode === 'SEA' ? 3 : 2;
            const defaultVolumePct = mode === 'SEA' ? 10 : 3;
            const wPct = !isNaN(weightPct) ? weightPct : defaultWeightPct;
            const vPct = !isNaN(volumePct) ? volumePct : defaultVolumePct;

            // Chargeable for per-kg attribute fees (airline_handling is PER KG)
            // AIR: max(Gr.Wt M/E, Vol Wt /6000); COURIER: max(Gr.Wt M/E, Vol×200); SEA: Gr.Wt M/E
            const getAttrChargeableWeight = (shipmentMode, wt, vol) => {
                const w = parseFloat(wt) || 0;
                const v = parseFloat(vol) || 0;
                const grWtME = w * (1 + wPct / 100);
                const volME = v * (1 + vPct / 100);
                if (shipmentMode === 'AIR') {
                    const volWt = (volME * 1000000) / 6000; // = volME * 166.666...
                    return roundOffChargeable(Math.max(grWtME, volWt), 0.5);
                }
                if (shipmentMode === 'SEA') {
                    return roundOffChargeable(grWtME, 0.5);
                }
                if (shipmentMode === 'LAND') {
                    return roundOffChargeable(landPackageChargeable(products, wPct, vPct), 0.5);
                }
                // COURIER — same per-package pick as the quote, then nearest kg
                return roundOffChargeable(courierPackageChargeable(products, wPct, vPct), 0.5);
            };

            const attrChargeableWeight = getAttrChargeableWeight(mode, totalWt, totalVol);

            let totalAttributeCost = 0;
            let attributeBreakdown = {};
            let attributeBreakdownMeta = {};

            const addFeesForAttribute = (attrName, displayName) => {
                const attrConfig = attributeCharges.find(a => a.attribute_name && a.attribute_name.toLowerCase() === attrName.toLowerCase());
                if (!attrConfig || !attrConfig.data) return;
                const typeData = attrConfig.data.find(
                    d => String(d.shipment_type || '').toUpperCase() === mode
                );
                if (!typeData) return;
                const dg_declaration = parseFloat(typeData.dg_declaration) || 0;
                const dangerous_goods_fee = parseFloat(typeData.dangerous_goods_fee) || 0;
                const dg_packing = parseFloat(typeData.dg_packing) || 0;
                const airline_handling = parseFloat(typeData.airline_handling) || 0;
                const airlineHandlingFee = airline_handling * attrChargeableWeight;
                const fee = dg_declaration + dangerous_goods_fee + dg_packing + airlineHandlingFee;
                if (fee > 0) {
                    totalAttributeCost += fee;
                    const label = `Additional Fees (${displayName})`;
                    attributeBreakdown[label] = money(fee, 2);
                    attributeBreakdownMeta[label] = {
                        mode,
                        dg_declaration,
                        dangerous_goods_fee,
                        dg_packing,
                        airline_handling,
                        chargeableWeight: attrChargeableWeight,
                        airlineHandlingFee,
                        total: fee,
                    };
                }
            };

            if (shipmentHasPerfume) {
                const hasS = attributeCharges.some(a => a.attribute_name && a.attribute_name.toLowerCase() === 'perfumes');
                addFeesForAttribute(hasS ? 'perfumes' : 'perfume', 'Perfume');
            }
            if (shipmentHasBattery) addFeesForAttribute('battery', 'Contains Battery');
            if (shipmentHasCosmetic) {
                const hasS = attributeCharges.some(a => a.attribute_name && a.attribute_name.toLowerCase() === 'cosmetics');
                addFeesForAttribute(hasS ? 'cosmetics' : 'cosmetic', 'Cosmetics');
            }
            if (shipmentHasMagnet) addFeesForAttribute('magnet', 'Contains Magnet');

            const insuranceCost = computedInsurance;
            const chargeableWeight = attrChargeableWeight;

            // Keep duty/vat on products so breakdown % labels match dutyApiData / HS validate
            const productsWithDuty = products.map(p => {
                const mi = mappedItems.find(m => String(m.hs_code) === String(p.hsCode));
                if (!mi) return p;
                return {
                    ...p,
                    dutyRate: mi.dutyRate ?? p.dutyRate,
                    vatRate: mi.vatRate ?? p.vatRate,
                    duty: mi.dutyRate ?? p.duty,
                    vat: mi.vatRate ?? p.vat
                };
            });

            setResults(prev => ({
                ...prev,
                [mode]: {
                    ...allData,
                    ...responseData,
                    discountRule,
                    computedDuty,
                    dutyApiData,
                    totalPackingCost,
                    packingMaterial: totalPackingCost,
                    freightDelta,
                    freightCompare,
                    packingBreakdown,
                    packingBreakdownMeta,
                    totalAttributeCost,
                    attributeBreakdown,
                    attributeBreakdownMeta,
                    insuranceCost,
                    totalGoodsValue,
                    chargeableWeight,
                    products: productsWithDuty,
                    totalWt,
                    totalVol,
                    marginWeightPct: wPct,
                    marginVolumePct: vPct
                }
            }));
            setActiveTab(mode);
        } catch (error) {
            setResults(prev => ({ ...prev, [mode]: error.toString() }));
            setActiveTab(mode);
        }
        setLoading(null);
    };

    const renderAirResultTable = (data) => {
        if (!data) return null;
        if (typeof data === 'string') return <div className="alert alert-danger">{data}</div>;
        if (typeof data !== 'object') return null;

        const renderDeliveryMode = (deliveryData, serviceType, serviceKey) => {
            if (!deliveryData || typeof deliveryData !== 'object' || Array.isArray(deliveryData) || Object.keys(deliveryData).length === 0) return null;

            const isDoor = serviceType === 'Door to Door';
            const destCountry = data.country?.destinationResult || {};
            if (isDoor && destCountry.is_air_door_available === false) return null;
            if (!isDoor && destCountry.is_air_port_available === false) return null;

            // Meta Details — every product package (count, size, weight)
            const dest = locations.to?.city || locations.to?.country || '';
            const pkgSummary = summarizePackages(data.products);
            const pkg = pkgSummary.count;
            const dims = pkgSummary.text;

            const grWt = data.totalWt || 0;
            const wPct = parseFloat(data.marginWeightPct ?? data.margin?.weight_percentage ?? 2);
            const vPct = parseFloat(data.marginVolumePct ?? data.margin?.volume_percentage ?? 3);
            const grWtME = grWt * (1 + wPct / 100);

            const vol = data.totalVol || 0;
            const volME = vol * (1 + vPct / 100);
            const volWt = (volME * 1000000) / 6000;

            const chWt = Math.max(grWtME, volWt);
            const apiChWt = parseFloat(deliveryData.rate_basis?.chargeable_weight);
            const roundOffChWt = apiChWt > 0 ? apiChWt : roundOffChargeable(chWt, 0.5);
            const goodsValue = data.totalGoodsValue || 0;

            // Extracting Charges
            const raw = deliveryData;

            // Find these keys from the raw payload if they exist, otherwise fallback
            const awbFee = parseFloat(raw.awb_fee || 0);
            const airlineHandling = parseFloat(raw.airline_handling || 0);
            const customs = parseFloat(raw.customs_docs || raw.customs || 0);
            const transport1 = parseFloat(raw.transport_store_to_s2d || raw.transport_1 || 0);
            const transport2 = parseFloat(raw.transport_s2d_to_apt || raw.transport_2 || 0);
            const handlingInOut = parseFloat(raw.min_handling_in_out || raw.handling_in_out || 0);
            const freight = parseFloat(raw.fregiht_amount || raw.freight || raw.base_freight || 0);
            const fuel = parseFloat(raw.fuel_amount || raw.fuel || 0);
            // DTHC comes as door_airline_charges from API (legacy key: dthc)
            const dthc = parseFloat(raw.door_airline_charges || raw.dthc || 0);
            const doFee = parseFloat(raw.deliver_order_fee || raw.do_fee || 0);
            const customsClearance = parseFloat(raw.customs_clearance || 0);
            const bank = parseFloat(raw.bank_charges || raw.bank || 0);
            const quarantine = parseFloat(raw.quarantine_fee || raw.quarantine || 0);
            const transport = parseFloat(raw.transport_tariff || raw.transport || 0);
            const inland = parseFloat(raw.destination_inland_amount || raw.inland || 0);

            // Always sum line items — API total_amount historically omitted DTHC + customs_clearance
            let total = awbFee + airlineHandling + customs + transport1 + transport2 + handlingInOut + freight + fuel;
            if (isDoor) {
                total += dthc + doFee + customsClearance + bank + quarantine + transport + inland;
            }

            const tierMargin = pickTierMargin(profitMargins, selectedTierIndex, total, goodsValue);
            const marginPct = tierMargin ? tierMargin.marginPct : 30;
            const minMarginAmt = tierMargin ? tierMargin.minMarginAmt : 0;
            let profit = total * (marginPct / 100);
            if (profit < minMarginAmt) {
                profit = minMarginAmt;
            }

            const totalWithMargins = total + profit;
            const roundOff = Math.round(totalWithMargins);

            const mpsCaf = parseFloat(systemSettings?.marketplace_settings?.caf ?? 5) || 0;
            const doorCaf = computeDoorCaf(totalWithMargins, mpsCaf);
            const caf = isDoor ? doorCaf.caf : 0;
            const totalFreight = isDoor ? doorCaf.totalFreight : roundOff;
            const freightDiff = Number(data.freightDelta?.[serviceKey]) || 0;
            const freightPair = data.freightCompare?.[serviceKey] || null;
            const woodenBox = packingChargeFor(data, serviceKey);
            const battery = data.totalAttributeCost || 0;

            const totalFrt = totalFreight + woodenBox + battery;

            let dutyTaxesUI = null;
            if (data.dutyApiData && data.dutyApiData.status) {
                const api = data.dutyApiData;
                const mps = systemSettings?.marketplace_settings || {};
                const insRate = parseFloat(mps.duty_tax_insurance_per !== undefined ? mps.duty_tax_insurance_per : 1);
                const freightRate = parseFloat(mps.duty_tax_freight_per !== undefined ? mps.duty_tax_freight_per : 25);
                const firstItem = data.products?.[0] || {};
                const apiItem = api.items?.[0] || {};
                const dynDutyRate = parseFloat(apiItem.duty_rate ?? firstItem.dutyRate ?? firstItem.duty ?? firstItem.hs_duty) || 0;
                const dynVatRate = parseFloat(apiItem.vat_rate ?? firstItem.vatRate ?? firstItem.vat ?? firstItem.hs_vat) || 0;

                dutyTaxesUI = (
                    <>
                        <tr className="bg-light"><td colSpan="2" className="font-weight-bold text-success mt-2">Duty/Taxes Calculation</td></tr>
                        <tr><td className="text-muted">Cost of Goods</td><td className="font-weight-bold">{money(api.total_goods_value)}</td></tr>
                        <tr><td className="text-muted">Insurance ({insRate}%)</td><td className="font-weight-bold">{money(api.insurance_cost)}</td></tr>
                        <tr><td className="text-muted">Freight ({freightRate}%)</td><td className="font-weight-bold">{money(api.freight_cost)}</td></tr>
                        <tr><td className="text-muted">CIF Value</td><td className="font-weight-bold">{money(api.cif_value)}</td></tr>
                        <tr><td className="text-muted">Duty ({dynDutyRate}%)</td><td className="font-weight-bold">{money(api.duty_amount)}</td></tr>
                        <tr><td className="text-muted">Taxes ({dynVatRate}%)</td><td className="font-weight-bold">{money(api.tax_amount)}</td></tr>
                        <tr><td className="text-muted">Duty + Taxes</td><td className="font-weight-bold">{money(api.total_duty_and_taxes)}</td></tr>
                        <tr>
                            <ChargeLabel
                                label={`CAF on Duty (${parseFloat(systemSettings?.marketplace_settings?.caf ?? 5)}%)`}
                                hint={`${parseFloat(systemSettings?.marketplace_settings?.caf ?? 5)}% × (Duty + Taxes)`}
                            />
                            <td className="font-weight-bold">{money(api.caf_amount)}</td>
                        </tr>
                        <tr><td className="text-muted">Additional Duty Fee</td><td className="font-weight-bold">{money(api.additional_fee)}</td></tr>
                        <tr style={{ backgroundColor: '#dcfce7' }} className="font-weight-bold">
                            <td style={{ color: '#166534' }}>Total Duty & Taxes</td>
                            <td style={{ color: '#166534' }}>{money(api.duty_estimate)}</td>
                        </tr>
                    </>
                );
            }

            return (
                <div key={serviceType} className="card mb-4 shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <div className="card-header text-white font-weight-bold d-flex justify-content-between align-items-center" style={{ backgroundColor: '#2563eb' }}>
                        <span className="text-uppercase">{serviceType} - Test 1 Breakdown</span>
                        <span className="d-flex align-items-center" style={{ gap: 8 }}>
                        <button type="button" className="btn btn-sm btn-light" onClick={() => setQuoteDoc({ title: `${serviceType} Test 1`, mode: 'AIR', from: locations.from?.description || locations.from?.city || 'Origin', to: dest || 'Destination', lines: [{ label: 'Freight total', value: money(totalFrt, 2) }], beforeProfit: money(total, 2), profit: money(profit, 2), roundOff: String(roundOff), freight: money(totalFrt, 2), duty: data.dutyApiData?.status ? money(Number(data.dutyApiData.duty_estimate) || 0, 2) : 'At actuals', date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) })}>View quotation</button>
                        <button type="button" className="btn btn-sm btn-outline-light" onClick={() => shareQuote({ title: `${serviceType} Test 1`, mode: 'AIR', from: locations.from?.description || locations.from?.city || 'Origin', to: dest || 'Destination', lines: [{ label: 'Freight total', value: money(totalFrt, 2) }], freight: money(totalFrt, 2), duty: 'At actuals', date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) })}>Share</button>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto', display: 'inline-block', fontWeight: 'bold', marginLeft: '10px' }}
                            value={selectedTierIndex}
                            onChange={(e) => setSelectedTierIndex(Number(e.target.value))}
                        >
                            {TIERS.map((t, idx) => (
                                <option key={idx} value={idx}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                        </span>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm table-bordered m-0">
                            <tbody>
                                <tr className="bg-light"><td colSpan="2" className="font-weight-bold text-primary">Meta Details</td></tr>
                                <tr><td width="50%" className="text-muted">Mode</td><td className="font-weight-bold">Airfrt</td></tr>
                                <tr><td className="text-muted">Dest</td><td className="font-weight-bold">{dest}</td></tr>
                                <tr><td className="text-muted">Service Type</td><td className="font-weight-bold">{serviceType}</td></tr>
                                <tr><td className="text-muted">Pkg</td><td className="font-weight-bold">{pkg}</td></tr>
                                <tr><td className="text-muted">Dims</td><td className="font-weight-bold">{dims}</td></tr>
                                <tr><td className="text-muted">Gr.Wt</td><td className="font-weight-bold">{money(grWt, 2)}</td></tr>
                                <tr><ChargeLabel label={`Gr.Wt with M/E (${wPct}%)`} hint={`${grWt} × (1 + ${wPct}%)`} from="Margin settings: weight margin % added on the gross weight of every package." /><td className="font-weight-bold">{money(grWtME, 2)}</td></tr>
                                <tr><td className="text-muted">Vol</td><td className="font-weight-bold">{money(vol, 3)}</td></tr>
                                <tr><ChargeLabel label={`Vol with M/E (${vPct}%)`} hint={`${vol} × (1 + ${vPct}%)`} from="Margin settings: volume margin % added on the CBM of every package." /><td className="font-weight-bold">{money(volME, 3)}</td></tr>
                                <tr><ChargeLabel label="Vol Wt" hint="Vol M/E × 1,000,000 / 6,000" from="Air volumetric rule: CBM with margin converted to kg using divisor 6,000." /><td className="font-weight-bold">{money(volWt, 2)}</td></tr>
                                <tr><ChargeLabel label="Ch Wt" hint="max(Gr.Wt M/E, Vol Wt)" from="Chargeable weight is the higher of gross weight with margin and volumetric weight." /><td className="font-weight-bold">{money(chWt, 2)}</td></tr>
                                <tr><ChargeLabel label="Round Off Ch Wt" hint={apiChWt > 0 ? 'Weight used for freight and fuel, rounded to the nearest kg.' : null} from="Same rounded weight the air quote uses to price freight and fuel." /><td className="font-weight-bold">{roundOffChWt}</td></tr>
                                <tr><td className="text-muted">Goods Value</td><td className="font-weight-bold">{goodsValue}</td></tr>

                                <tr className="bg-light"><td colSpan="2" className="font-weight-bold text-danger mt-2">Calculations Breakdown</td></tr>
                                {(() => {
                                    const breakdownRows = [];
                                    const freightAmt = parseFloat(raw.fregiht_amount || raw.freight || raw.base_freight || 0);
                                    Object.entries(raw).forEach(([key, value]) => {
                                        if (key === 'discountRule') return;
                                        if (key.startsWith('discount_') && key.includes('grant_total')) return;
                                        if (key.includes('name') || key.includes('_tt') || key.includes('id') || key.includes('_id') || key === 'tier_name') return;
                                        if (key.includes('margin') || key.includes('grant_total') || key === 'grand_total') return;
                                        if (['overweightFee', 'dutyCafAmount', 'finalFreight', 'totalDutyEst', 'baseFreight', 'dutyVal', 'vatVal', 'cafPercent', 'total_amount', 'calculated_duty_est', 'calculated_overweight_fee', 'calculated_caf_fee', 'port_name'].includes(key)) return;

                                        const val = parseFloat(value);
                                        if (!isNaN(val)) {
                                            const enriched = enrichChargeLabel(key, val, {
                                                data,
                                                details: raw,
                                                freightAmt,
                                                serviceType,
                                                chargeableKg: roundOffChWt,
                                                shipmentMode: 'AIR',
                                                destinationPlace: [locations.to?.city, locations.to?.country].filter(Boolean).join(', '),
                                            });
                                            breakdownRows.push({ ...enriched, val });
                                        }
                                    });
                                    return breakdownRows.map((r, i) => (
                                        <tr key={i}>
                                            <ChargeLabel label={r.label} hint={r.hint} from={r.from} />
                                            <td>{money(r.val, 2)}</td>
                                        </tr>
                                    ));
                                })()}
                                <tr style={{ backgroundColor: '#fef3c7' }} className="font-weight-bold">
                                    <td style={{ color: '#92400e' }}>Total (Before Profit)</td>
                                    <td style={{ color: '#92400e' }}>{money(total, 2)}</td>
                                </tr>
                                <tr>
                                    <ChargeLabel
                                        label="Profit"
                                        hint={`${marginPct}% × ${money(total, 2)}${minMarginAmt > 0 ? ` (min ${minMarginAmt})` : ''} · Tier ${selectedTierIndex + 1}`}
                                        from="Tier profit table. The band is chosen from the total before profit and the goods value. If the percentage is below the minimum profit, the minimum is used."
                                    />
                                    <td className="font-weight-bold text-success">{money(profit, 2)} <span className="text-muted font-weight-normal" style={{ fontSize: '0.75rem' }}>({marginPct}%)</span></td>
                                </tr>
                                <tr><td className="text-muted">Total with Margins</td><td className="font-weight-bold">{money(totalWithMargins, 2)}</td></tr>
                                <tr style={{ backgroundColor: '#dbeafe' }} className="font-weight-bold">
                                    <td style={{ color: '#1e40af' }}>Round off</td>
                                    <td style={{ color: '#1e40af' }}>{roundOff}</td>
                                </tr>
                                {isDoor && (
                                    <>
                                        <tr>
                                            <ChargeLabel label={`CAF (${mpsCaf}%)`} hint={`${mpsCaf}% × Round Off ${roundOff} · Door only`} from="Marketplace CAF setting. Charged only on door delivery, on the rounded total." />
                                            <td>{money(caf, 2)}</td>
                                        </tr>
                                        <tr className="bg-light font-weight-bold text-dark"><td className="text-muted">Total Freight</td><td>{money(totalFreight, 2)}</td></tr>
                                    </>
                                )}
                                {renderPackingAndAttributeRows(data, true, freightDiff, freightPair)}
                                <tr className="bg-success text-white font-weight-bold"><td>Total Frt</td><td>{money(totalFrt, 2)}</td></tr>

                                {isDoor && dutyTaxesUI}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        };

        return (
            <div>
                {renderDeliveryMode(data.port_delivery, 'Door to Port', 'port_delivery')}
                {renderDeliveryMode(data.door_delivery, 'Door to Door', 'door_delivery')}
            </div>
        );
    };

    const renderSeaResultTable = (data) => {
        if (!data) return null;
        if (typeof data === 'string') return <div className="alert alert-danger">{data}</div>;
        if (typeof data !== 'object') return null;

        const hasSeaDeliveryCharges = (deliveryData) => {
            if (!deliveryData || typeof deliveryData !== 'object') return false;
            // Ignore marketplace/duty-only keys — empty {} or duty-only should not render a card
            const freight = Number(deliveryData.fregiht_amount || deliveryData.freight || 0);
            const grant = Number(
                deliveryData.door_grant_total ||
                deliveryData.port_grant_total ||
                deliveryData.total_amount ||
                0
            );
            const bl = Number(deliveryData.bl_fee || deliveryData.b_l_fee || 0);
            return freight > 0 || grant > 0 || bl > 0;
        };

        const renderDeliveryMode = (deliveryData, serviceType, serviceKey) => {
            // Match Air RateChecker + FreightCalculator: skip empty / unavailable door payloads
            if (!deliveryData || typeof deliveryData !== 'object' || Array.isArray(deliveryData) || Object.keys(deliveryData).length === 0) return null;
            if (!hasSeaDeliveryCharges(deliveryData)) return null;

            const isDoor = serviceType === 'Door to Door';
            const destCountry = data.country?.destinationResult || {};
            if (isDoor && destCountry.is_sea_door_available === false) return null;
            if (!isDoor && destCountry.is_sea_port_available === false) return null;

            const d = deliveryData;

            // --- Meta details — every product package (count, size, weight) ---
            const dest = locations.to?.city || locations.to?.country || '';
            const pkgSummary = summarizePackages(data.products);
            const pkg = pkgSummary.count;
            const dimsStr = pkgSummary.text;

            const grWt = data.totalWt || 0;
            const wPct = parseFloat(data.marginWeightPct ?? data.margin?.weight_percentage ?? 3);
            const vPct = parseFloat(data.marginVolumePct ?? data.margin?.volume_percentage ?? 10);
            const grWtME = grWt * (1 + wPct / 100);
            const vol = data.totalVol || 0;
            const volWtME = vol * (1 + vPct / 100);
            const seaRateRow = isDoor ? data.sea_freight_door : (serviceType.toLowerCase().includes('consol') ? data.sea_consolidation_freight : data.sea_freight);
            const minCbm = Number(seaRateRow?.minimum_amount);
            const chargeableVol = (!serviceType.toLowerCase().includes('consol') && Number.isFinite(minCbm) && minCbm > volWtME) ? minCbm : volWtME;
            const chargeableVolLabel = Number(money(Number(chargeableVol), 4));
            const goodsValue = data.totalGoodsValue || 0;

            // --- Calculations ---
            // API key is bl_fee (legacy alias: b_l_fee)
            const blFee = Number(d.bl_fee || d.b_l_fee) || 0;
            const customsDocs = Number(d.customs_docs) || 0;
            const weightment = Number(d.weightment_charges) || 0;
            const transport1 = Number(d.transport_store_to_s2d) || 0;
            const transport2 = Number(d.transport_s2d_to_seaport) || 0;
            const handling = Number(d.handling_in_out) || 0;
            const freight = Number(d.fregiht_amount) || 0;
            const additionalFee = Number(d.additional_fee) || 0;

            let nvoccFixed = 0, nvoccPerCbm = 0, portCharges = 0, customsClearance = 0, bank = 0, quarantine = 0, transportation = 0, inland = 0;

            if (isDoor) {
                nvoccFixed = Number(d.NVOCC_charges_fixed) || 0;
                nvoccPerCbm = Number(d.NVOCC_charges_per_cmb || d.NVOCC_charges_per_cbm) || 0;
                portCharges = Number(d.port_charges) || 0;
                customsClearance = Number(d.customs_clearance) || 0;
                bank = Number(d.bank_charges) || 0;
                quarantine = Number(d.quarantine_fee) || 0;
                transportation = Number(d.transport_tariff) || 0;
                inland = Number(d.destination_inland_amount) || 0;
            }

            // Always sum line items — API total_amount previously used wrong keys / subtracted TT days
            let total = blFee + customsDocs + weightment + transport1 + transport2 + handling + freight + additionalFee;
            if (isDoor) {
                total += nvoccFixed + nvoccPerCbm + portCharges + customsClearance + bank + quarantine + transportation + inland;
            }

            const tierNum = selectedTierIndex === 0 ? 1 : (selectedTierIndex === 1 ? 2 : 3);
            const tierMargin = pickTierMargin(profitMargins, selectedTierIndex, total, goodsValue);
            const marginPct = tierMargin ? tierMargin.marginPct : 30;
            const minMarginAmt = tierMargin ? tierMargin.minMarginAmt : 0;
            let profit = total * (marginPct / 100);
            if (profit < minMarginAmt) {
                profit = minMarginAmt;
            }
            const totalWithMargins = total + profit;
            const roundOff = Math.round(totalWithMargins);

            const mpsCafSea = parseFloat(systemSettings?.marketplace_settings?.caf ?? 5) || 0;
            const doorCaf = computeDoorCaf(totalWithMargins, mpsCafSea);
            const caf = isDoor ? doorCaf.caf : 0;
            const totalFreight = isDoor ? doorCaf.totalFreight : roundOff;
            const freightDiff = Number(data.freightDelta?.[serviceKey]) || 0;
            const freightPair = data.freightCompare?.[serviceKey] || null;
            const woodenBox = packingChargeFor(data, serviceKey);
            const battery = data.totalAttributeCost || 0;
            const totalFrt = totalFreight + woodenBox + battery;

            // Duty & Taxes (Dynamic) - API data takes priority
            let dutyTaxesUI = null;
            if (data.dutyApiData && data.dutyApiData.status) {
                const api = data.dutyApiData;
                const mps = systemSettings?.marketplace_settings || {};
                const insRate = parseFloat(mps.duty_tax_insurance_per !== undefined ? mps.duty_tax_insurance_per : 1);
                const freightRate = parseFloat(mps.duty_tax_freight_per !== undefined ? mps.duty_tax_freight_per : 25);
                const firstItem = data.products?.[0] || {};
                const apiItem = api.items?.[0] || {};
                const dynDutyRate = parseFloat(apiItem.duty_rate ?? firstItem.dutyRate ?? firstItem.duty ?? firstItem.hs_duty) || 0;
                const dynVatRate = parseFloat(apiItem.vat_rate ?? firstItem.vatRate ?? firstItem.vat ?? firstItem.hs_vat) || 0;

                dutyTaxesUI = (
                    <>
                        <tr className="bg-light"><td colSpan="2" className="font-weight-bold text-success mt-2">Duty/Taxes Calculation</td></tr>
                        <tr><td className="text-muted">Cost of Goods</td><td className="font-weight-bold">{money(api.total_goods_value)}</td></tr>
                        <tr><td className="text-muted">Insurance ({insRate}%)</td><td className="font-weight-bold">{money(api.insurance_cost)}</td></tr>
                        <tr><td className="text-muted">Freight ({freightRate}%)</td><td className="font-weight-bold">{money(api.freight_cost)}</td></tr>
                        <tr><td className="text-muted">CIF Value</td><td className="font-weight-bold">{money(api.cif_value)}</td></tr>
                        <tr><td className="text-muted">Duty ({dynDutyRate}%)</td><td className="font-weight-bold">{money(api.duty_amount)}</td></tr>
                        <tr><td className="text-muted">Taxes ({dynVatRate}%)</td><td className="font-weight-bold">{money(api.tax_amount)}</td></tr>
                        <tr><td className="text-muted">Duty + Taxes</td><td className="font-weight-bold">{money(api.total_duty_and_taxes)}</td></tr>
                        <tr>
                            <ChargeLabel
                                label={`CAF on Duty (${parseFloat(systemSettings?.marketplace_settings?.caf ?? 5)}%)`}
                                hint={`${parseFloat(systemSettings?.marketplace_settings?.caf ?? 5)}% × (Duty + Taxes)`}
                            />
                            <td className="font-weight-bold">{money(api.caf_amount)}</td>
                        </tr>
                        <tr><td className="text-muted">Additional Duty Fee</td><td className="font-weight-bold">{money(api.additional_fee)}</td></tr>
                        <tr style={{ backgroundColor: '#dcfce7' }} className="font-weight-bold">
                            <td style={{ color: '#166534' }}>Total Duty & Taxes</td>
                            <td style={{ color: '#166534' }}>{money(api.duty_estimate)}</td>
                        </tr>
                    </>
                );
            } else if (d && d.totalDutyEst !== undefined && d.totalDutyEst > 0) {
                dutyTaxesUI = (
                    <>
                        <tr className="bg-light"><td colSpan="2" className="font-weight-bold text-info mt-2">Duty/Taxes Calculation</td></tr>
                        <tr><td className="text-muted">Cost of Goods</td><td className="font-weight-bold">{money(goodsValue, 2)}</td></tr>
                        <tr><td className="text-muted">Overweight Fee</td><td className="font-weight-bold">{money((d.overweightFee || 0), 2)}</td></tr>
                        <tr><td className="text-muted">Duty Value</td><td className="font-weight-bold">{money((d.dutyVal || 0), 4)}</td></tr>
                        <tr><td className="text-muted">Taxes Value (VAT)</td><td className="font-weight-bold">{money((d.vatVal || 0), 4)}</td></tr>
                        <tr><td className="text-muted">Duty + Taxes</td><td className="font-weight-bold">{money(((d.dutyVal || 0) + (d.vatVal || 0)), 4)}</td></tr>
                        <tr><td className="text-muted">CAF on Duty/Taxes ({(d.cafPercent || 0)}%)</td><td className="font-weight-bold">{money((d.dutyCafAmount || 0), 4)}</td></tr>
                        <tr><td className="text-muted">Additional Duty Fee</td><td className="font-weight-bold">{money((0), 2)}</td></tr>
                        <tr style={{ backgroundColor: '#dcfce7' }} className="font-weight-bold">
                            <td style={{ color: '#166534' }}>Total Duty & Taxes</td>
                            <td style={{ color: '#166534' }}>{money((d.totalDutyEst || 0), 4)}</td>
                        </tr>
                    </>
                );
            }

            return (
                <div key={serviceType} className="card mb-4 shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <div className="card-header text-white font-weight-bold d-flex justify-content-between align-items-center" style={{ backgroundColor: '#2563eb' }}>
                        <span className="text-uppercase">{serviceType} - Sea Breakdown</span>
                        <span className="d-flex align-items-center" style={{ gap: 8 }}>
                        <button type="button" className="btn btn-sm btn-light" onClick={() => setQuoteDoc({ title: `${serviceType} Sea`, mode: 'SEA', from: locations.from?.description || locations.from?.city || 'Origin', to: dest || 'Destination', lines: [{ label: 'Freight total', value: money(totalFrt, 2) }], beforeProfit: money(total, 2), profit: money(profit, 2), roundOff: String(roundOff), freight: money(totalFrt, 2), duty: data.dutyApiData?.status ? money(Number(data.dutyApiData.duty_estimate) || 0, 2) : 'At actuals', date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) })}>View quotation</button>
                        <button type="button" className="btn btn-sm btn-outline-light" onClick={() => shareQuote({ title: `${serviceType} Sea`, mode: 'SEA', from: locations.from?.description || locations.from?.city || 'Origin', to: dest || 'Destination', lines: [{ label: 'Freight total', value: money(totalFrt, 2) }], freight: money(totalFrt, 2), duty: 'At actuals', date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) })}>Share</button>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto', display: 'inline-block', fontWeight: 'bold', marginLeft: '10px' }}
                            value={selectedTierIndex}
                            onChange={(e) => setSelectedTierIndex(Number(e.target.value))}
                        >
                            {TIERS.map((t, idx) => (
                                <option key={idx} value={idx}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                        </span>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-sm table-bordered m-0">
                            <tbody>
                                {/* Meta Details */}
                                <tr className="bg-light"><td colSpan="2" className="font-weight-bold text-primary">Meta Details</td></tr>
                                <tr><td width="50%" className="text-muted">Mode</td><td className="font-weight-bold">Sea Frt</td></tr>
                                <tr><td className="text-muted">Dest</td><td className="font-weight-bold">{dest}</td></tr>
                                <tr><td className="text-muted">Service Type</td><td className="font-weight-bold">{serviceType}</td></tr>
                                <tr><td className="text-muted">Pkg</td><td className="font-weight-bold">{pkg}</td></tr>
                                <tr><td className="text-muted">Dims</td><td className="font-weight-bold">{dimsStr}</td></tr>
                                <tr><td className="text-muted">Gr.Wt</td><td className="font-weight-bold">{money(grWt, 2)}</td></tr>
                                <tr><ChargeLabel label={`Gr.Wt with M/E (${wPct}%)`} hint={`${grWt} × (1 + ${wPct}%)`} from="Margin settings: weight margin % on the gross weight of every package." /><td className="font-weight-bold">{money(grWtME, 2)}</td></tr>
                                <tr><td className="text-muted">Vol</td><td className="font-weight-bold">{money(vol, 2)}</td></tr>
                                <tr><ChargeLabel label={`Vol with M/E (${vPct}%)`} hint={`${vol} × (1 + ${vPct}%)`} from="Margin settings: volume margin % on the CBM of every package." /><td className="font-weight-bold">{money(volWtME, 3)}</td></tr>
                                <tr><ChargeLabel label="Chargeable Vol" hint={chargeableVolLabel !== Number(money(Number(volWtME), 4)) ? `Volume with margin ${Number(money(Number(volWtME), 4))} CBM is below the rate minimum ${minCbm} CBM, so freight uses ${chargeableVolLabel} CBM.` : `${vol} × (1 + ${vPct}%) = ${chargeableVolLabel} CBM.`} from="Sea quote prices freight on this chargeable CBM." /><td className="font-weight-bold">{chargeableVolLabel}</td></tr>
                                <tr><td className="text-muted">Goods Value</td><td className="font-weight-bold">{money(goodsValue, 2)}</td></tr>

                                {/* Calculations */}
                                <tr className="bg-light"><td colSpan="2" className="font-weight-bold text-danger mt-2">Calculations Breakdown</td></tr>
                                {(() => {
                                    const breakdownRows = [];
                                    const freightAmt = parseFloat(d.fregiht_amount || d.freight || 0);
                                    Object.entries(d).forEach(([key, value]) => {
                                        if (key === 'discountRule') return;
                                        if (key.startsWith('discount_') && key.includes('grant_total')) return;
                                        if (key.includes('name') || key.includes('_tt') || key.includes('id') || key.includes('_id') || key === 'tier_name') return;
                                        if (key.includes('margin') || key.includes('grant_total') || key === 'grand_total') return;
                                        if (['overweightFee', 'dutyCafAmount', 'finalFreight', 'totalDutyEst', 'baseFreight', 'dutyVal', 'vatVal', 'cafPercent', 'total_amount', 'calculated_duty_est', 'calculated_overweight_fee', 'calculated_caf_fee', 'port_name'].includes(key)) return;

                                        const val = parseFloat(value);
                                        if (!isNaN(val)) {
                                            const enriched = enrichChargeLabel(key, val, {
                                                data,
                                                details: d,
                                                freightAmt,
                                                serviceType,
                                                chargeableCbm: chargeableVolLabel,
                                                shipmentMode: 'SEA',
                                                destinationPlace: [locations.to?.city, locations.to?.country].filter(Boolean).join(', '),
                                            });
                                            breakdownRows.push({ ...enriched, val });
                                        }
                                    });
                                    return breakdownRows.map((r, i) => (
                                        <tr key={i}>
                                            <ChargeLabel label={r.label} hint={r.hint} from={r.from} />
                                            <td>{money(r.val, 2)}</td>
                                        </tr>
                                    ));
                                })()}
                                <tr style={{ backgroundColor: '#fef3c7' }} className="font-weight-bold">
                                    <td style={{ color: '#92400e' }}>Total (Before Profit)</td>
                                    <td style={{ color: '#92400e' }}>{money(total, 2)}</td>
                                </tr>
                                <tr>
                                    <ChargeLabel
                                        label="Profit"
                                        hint={`${marginPct}% × ${money(total, 2)}${minMarginAmt > 0 ? ` (min ${minMarginAmt})` : ''} · Tier ${tierNum}`}
                                        from="Tier profit table. The band is chosen from the total before profit and the goods value. If the percentage is below the minimum profit, the minimum is used."
                                    />
                                    <td className="font-weight-bold text-success">{money(profit, 2)} <span className="text-muted font-weight-normal" style={{ fontSize: '0.75rem' }}>({marginPct}% Tier {tierNum})</span></td>
                                </tr>
                                <tr><td className="text-muted">Total with Margins</td><td className="font-weight-bold">{money(totalWithMargins, 2)}</td></tr>
                                <tr style={{ backgroundColor: '#dbeafe' }} className="font-weight-bold">
                                    <td style={{ color: '#1e40af' }}>Round Off</td>
                                    <td style={{ color: '#1e40af' }}>{roundOff}</td>
                                </tr>
                                {isDoor && (
                                    <>
                                        <tr>
                                            <ChargeLabel label={`CAF (${mpsCafSea}%)`} hint={`${mpsCafSea}% × Round Off ${roundOff} · Door only`} from="Marketplace CAF setting. Charged only on door delivery, on the rounded total." />
                                            <td>{money(caf, 2)}</td>
                                        </tr>
                                        <tr className="bg-light font-weight-bold text-dark"><td className="text-muted">Total Freight</td><td>{money(totalFreight, 2)}</td></tr>
                                    </>
                                )}
                                {renderPackingAndAttributeRows(data, true, freightDiff, freightPair)}
                                <tr className="bg-success text-white font-weight-bold"><td>Total Frt</td><td>{money(totalFrt, 2)}</td></tr>

                                {isDoor && dutyTaxesUI}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        };

        return (
            <div>
                {renderDeliveryMode(data.port_delivery, 'Door to Port', 'port_delivery')}
                {renderDeliveryMode(data.door_delivery, 'Door to Door', 'door_delivery')}
            </div>
        );
    };

    const renderResultTable = (data) => {
        if (typeof data === 'string') {
            return <div className="alert alert-danger">{data}</div>;
        }
        if (!data || typeof data !== 'object') return null;

        const deliveryTypes = {};
        const destCountry = data.country?.destinationResult || {};

        if (data.port_delivery && typeof data.port_delivery === 'object' && !Array.isArray(data.port_delivery) && Object.keys(data.port_delivery).length > 0 && activeTab !== 'COURIER') {
            // LAND/generic: skip port card when country disables port
            if (activeTab === 'LAND' && destCountry.is_land_port_available === false) {
                // skip
            } else {
                const pd = { ...data.port_delivery };
                const rawFreight = parseFloat(pd.port_grant_total || 0);
                const { overweightFee, cafFee, finalFreight, totalDutyEst, dutyVal, vatVal, cafPercent, dutyCafAmount } = calculateMarketplaceCharges(rawFreight, data.totalGoodsValue, data.chargeableWeight, data.insuranceCost, data.products, systemSettings?.marketplace_settings);

                pd.calculated_overweight_fee = overweightFee;
                pd.calculated_caf_fee = cafFee;
                pd.totalDutyEst = (data.computedDuty !== undefined && data.computedDuty !== null) ? data.computedDuty : totalDutyEst;
                pd.dutyVal = dutyVal;
                pd.vatVal = vatVal;
                pd.cafPercent = cafPercent;
                pd.dutyCafAmount = dutyCafAmount;
                pd.packingServiceKey = 'port_delivery';
                pd.port_grant_total = money((finalFreight + packingChargeFor(data, 'port_delivery') + (data.totalAttributeCost || 0)), 2);
                deliveryTypes['Port Delivery'] = pd;
            }
        }
        // For COURIER, door_delivery is a copy of the cheapest carrier — prefer explicit *_courier_rates cards
        const hasCourierRateArrays = Object.keys(data).some(
            (k) => k.endsWith('_courier_rates') && Array.isArray(data[k]) && data[k].length > 0
        );
        if (data.door_delivery && typeof data.door_delivery === 'object' && !Array.isArray(data.door_delivery) && Object.keys(data.door_delivery).length > 0 && !(activeTab === 'COURIER' && hasCourierRateArrays)) {
            // Skip land door when country flag says unavailable (FC hides these too)
            if (activeTab === 'LAND' && destCountry.is_land_door_available === false) {
                // skip
            } else {
                const dd = { ...data.door_delivery };
                // LAND TNT: sum explicit charge keys (API total_amount historically omitted admin/fuel/handling)
                if (activeTab === 'LAND') {
                    const landDoorKeys = [
                        'be_fee', 'admin_fee', 'transport_store_to_s2d', 'handling_in_out',
                        'fregiht', 'freight', 'fuel_charges', 'peak_surcharge', 'additional_surcharge_amount'
                    ];
                    let landSum = 0;
                    landDoorKeys.forEach((k) => {
                        const v = parseFloat(dd[k]);
                        if (!isNaN(v)) landSum += v;
                    });
                    if (landSum > 0) dd.total_amount = landSum;
                }
                const rawFreight = parseFloat(dd.door_grant_total || dd.total_amount || 0);
                const { overweightFee, cafFee, finalFreight, totalDutyEst, dutyVal, vatVal, cafPercent, dutyCafAmount } = calculateMarketplaceCharges(rawFreight, data.totalGoodsValue, data.chargeableWeight, data.insuranceCost, data.products, systemSettings?.marketplace_settings);

                let actualDuty = (data.computedDuty !== undefined && data.computedDuty !== null) ? data.computedDuty : (totalDutyEst > 0 ? totalDutyEst : null);
                if (actualDuty === null || actualDuty === 0) {
                    const finalDuty = data?.final?.door_delivery?.total_duty_tax ?? dd.total_duty_tax;
                    if (finalDuty !== undefined && finalDuty !== null && finalDuty !== "Receiver to pay At Actuals on Arrival") {
                        const parsedTax = parseFloat(finalDuty);
                        if (!isNaN(parsedTax)) {
                            actualDuty = parsedTax;
                        }
                    }
                }
                dd.calculated_duty_est = actualDuty;
                // Marketplace overweight is not part of TNT land sheet (Over 30 kgs is carrier surcharge, already 0)
                dd.calculated_overweight_fee = activeTab === 'LAND' ? 0 : overweightFee;
                dd.calculated_caf_fee = cafFee;
                dd.totalDutyEst = actualDuty;
                dd.dutyVal = dutyVal;
                dd.vatVal = vatVal;
                dd.cafPercent = cafPercent;
                dd.dutyCafAmount = dutyCafAmount;
                dd.packingServiceKey = 'door_delivery';
                deliveryTypes['Door Delivery'] = dd;
            }
        }
        if (data.consolidation_delivery && typeof data.consolidation_delivery === 'object' && !Array.isArray(data.consolidation_delivery) && Object.keys(data.consolidation_delivery).length > 0 && activeTab !== 'COURIER') {
            if (activeTab === 'LAND' && destCountry.is_land_consolidation_available === false) {
                // skip
            } else {
                const cd = { ...data.consolidation_delivery };
                if (activeTab === 'LAND') {
                    const landConsolKeys = [
                        'fregiht', 'freight', 'pallet_charges', 'handling_in_out_express',
                        'transport_store_to_s2d', 'silla_border', 'batha_border',
                        'final_destination_charge', 'certificate_of_origin'
                    ];
                    let landSum = 0;
                    landConsolKeys.forEach((k) => {
                        const v = parseFloat(cd[k]);
                        if (!isNaN(v)) landSum += v;
                    });
                    if (landSum > 0) cd.total_amount = landSum;
                }
                const rawFreight = parseFloat(cd.consolidation_grant_total || cd.total_amount || 0);
                const { overweightFee, cafFee, finalFreight, totalDutyEst, dutyVal, vatVal, cafPercent, dutyCafAmount } = calculateMarketplaceCharges(rawFreight, data.totalGoodsValue, data.chargeableWeight, data.insuranceCost, data.products, systemSettings?.marketplace_settings);

                cd.calculated_overweight_fee = activeTab === 'LAND' ? 0 : overweightFee;
                cd.calculated_caf_fee = cafFee;
                cd.totalDutyEst = (data.computedDuty !== undefined && data.computedDuty !== null) ? data.computedDuty : totalDutyEst;
                cd.dutyVal = dutyVal;
                cd.vatVal = vatVal;
                cd.cafPercent = cafPercent;
                cd.dutyCafAmount = dutyCafAmount;
                cd.packingServiceKey = 'consolidation_delivery';
                deliveryTypes['Consolidation Delivery'] = cd;
            }
        }

        // Courier rates: margin from Tier tables (same as Air/Sea)
        const getCourierMargin = (cost, goodsVal) => {
            let marginPct = 30;
            let minMarginAmt = 0;
            if (profitMargins && profitMargins.length > 0) {
                const tierMargin = pickTierMargin(profitMargins, selectedTierIndex, cost, goodsVal || 0);
                if (tierMargin) {
                    marginPct = tierMargin.marginPct;
                    minMarginAmt = tierMargin.minMarginAmt;
                }
            } else {
                const marginData = data.door_profit_margin?.freight_data || data.profit_margin?.freight_data;
                if (marginData && cost) {
                    let marginAmt = cost * (marginData.percentage / 100);
                    if (marginData.minimum > marginAmt) marginAmt = marginData.minimum;
                    return { marginAmt, marginPct: marginData.percentage || 30 };
                }
            }
            let marginAmt = cost * (marginPct / 100);
            if (marginAmt < minMarginAmt) marginAmt = minMarginAmt;
            return { marginAmt, marginPct };
        };

        // Explicit courier charge keys (avoid Object.values summing % / flags / ids)
        const sumCourierRateCharges = (rate, courierName) => {
            const prefix = courierName.toLowerCase();
            const keys = [
                'be_fee', 'admin_fee', 'transport_store_to_s2d', 'handling_in_out',
                `${prefix}_fregiht`, `${prefix}_freight`,
                `${prefix}_fuel_charges`, `${prefix}_govt_tax_amount`,
                'additional_surcharge_amount', 'over_30_charge', 'over_30_kgs',
                'large_package_surcharge_amount', 'over_max_surcharge_amount',
                'additional_handling_charges', 'peak_surcharge', 'express_freight_shipment_amount',
                'express_shipment_amount', 'shipment_amount', 'additional_shipment_amount',
                // UPS-specific surcharge keys from quotes_ups_calculation
                'ups_additional_amount', 'ups_large_package_surcharge_amount', 'ups_over_max_surcharge_amount',
                'peak_demand_surcharge'
            ];
            let sum = 0;
            keys.forEach((k) => {
                const v = parseFloat(rate[k]);
                if (!isNaN(v)) sum += v;
            });
            // Fallback: API total_amount if explicit keys yielded nothing
            if (sum <= 0 && rate.total_amount !== undefined) {
                sum = parseFloat(rate.total_amount) || 0;
            }
            return sum;
        };

        const courierComparison = [];

        Object.keys(data).forEach(key => {
            if (key.endsWith('_courier_rates') && Array.isArray(data[key]) && data[key].length > 0) {
                const courierName = key.split('_')[0].toUpperCase();
                data[key].forEach((rate, index) => {
                    const serviceName = rate.service_name || rate.service_type || rate.name || `Option ${index + 1}`;
                    const rawCost = sumCourierRateCharges(rate, courierName);
                    const { marginAmt, marginPct } = getCourierMargin(rawCost, data.totalGoodsValue);
                    const totalWithMargins = rawCost + marginAmt;
                    const mpsCaf = parseFloat(systemSettings?.marketplace_settings?.caf ?? 5) || 0;
                    const { caf: cafFee, totalFreight } = computeDoorCaf(totalWithMargins, mpsCaf);
                    const packingKey = `${key}:${index}`;
                    const packing = packingChargeFor(data, packingKey);
                    const attrs = data.totalAttributeCost || 0;
                    const grantTotal = totalFreight + packing + attrs;

                    const discountRule = data.discountRule;
                    let finalPrice = grantTotal;

                    let actualDuty = (data.computedDuty !== undefined && data.computedDuty !== null) ? data.computedDuty : 0;
                    if (!actualDuty) {
                        if (courierName === 'FEDEX' && rate.fedex_govt_tax_amount !== undefined) actualDuty = parseFloat(rate.fedex_govt_tax_amount) || 0;
                        else if (courierName === 'UPS' && rate.ups_govt_tax_amount !== undefined) actualDuty = parseFloat(rate.ups_govt_tax_amount) || 0;
                        else if (courierName === 'DHL' && rate.dhl_govt_tax_amount !== undefined) actualDuty = parseFloat(rate.dhl_govt_tax_amount) || 0;
                    }
                    if (data.dutyApiData?.duty_estimate) actualDuty = parseFloat(data.dutyApiData.duty_estimate) || actualDuty;

                    let discountLabel = null;
                    let discountAmount = 0;

                    const goodsValue = data.totalGoodsValue || 0;
                    const insAmount = data.insuranceCost || 0;
                    finalPrice += insAmount + goodsValue;

                    if (discountRule) {
                        const isFlat = discountRule.discount_type === 'Flat Amount';
                        discountAmount = isFlat ? parseFloat(discountRule.discount_value) : grantTotal * (parseFloat(discountRule.discount_value) / 100);
                        finalPrice -= discountAmount;

                        discountLabel = discountRule.type || 'Discount';
                        if (discountRule.type === 'Country Specific Discount' && discountRule.condition_value) {
                            discountLabel += ` (Country: ${discountRule.condition_value})`;
                        } else if (discountRule.type === 'Product Specific Discount' && discountRule.condition_value) {
                            discountLabel += ` (HS Code: ${discountRule.condition_value})`;
                        } else if (discountRule.type === 'Volume Discount' && discountRule.condition_value) {
                            discountLabel += ` (Min: ${discountRule.condition_value})`;
                        } else if (discountRule.type === 'Promo Code Discount' && discountRule.condition_value) {
                            discountLabel += ` (Code: ${discountRule.condition_value})`;
                        }
                    }

                    courierComparison.push({
                        label: `${courierName} Courier`,
                        grantTotal,
                        baseFreight: rawCost,
                        otherCharges: packing + attrs + (totalFreight - Math.round(totalWithMargins)),
                        dutyEst: actualDuty,
                        discountLabel,
                        discountAmount,
                        finalPrice: parseFloat(money(finalPrice, 2)),
                        discountType: discountRule?.discount_type || 'Percentage',
                        discountVal: discountRule?.discount_value || 0
                    });

                    deliveryTypes[`${courierName} Courier - ${serviceName}`] = {
                        ...rate,
                        packingServiceKey: packingKey,
                        total_amount: rawCost,
                        courier_margin_amount: parseFloat(money(marginAmt, 2)),
                        courier_margin_pct: marginPct,
                        courier_caf_fee: cafFee,
                        courier_total_freight: totalFreight,
                        courier_grant_total: grantTotal,
                        calculated_duty_est: actualDuty,
                        calculated_overweight_fee: 0,
                        calculated_caf_fee: cafFee,
                        totalDutyEst: actualDuty,
                        cafPercent: mpsCaf,
                        dutyCafAmount: data.dutyApiData?.caf_amount || 0
                    };
                });
            }
        });

        if (Object.keys(deliveryTypes).length === 0) {
            return (
                <div className="alert alert-warning mb-4">
                    No calculated rates found. <br />
                    <small>Raw output available in console.</small>
                </div>
            );
        }

        // Courier comparison banner
        const courierBanner = courierComparison.length > 1 ? (() => {
            const sorted = [...courierComparison].sort((a, b) => a.finalPrice - b.finalPrice);
            return (
                <div className="card mb-4 shadow-sm border-0" style={{ borderRadius: '12px', borderLeft: '4px solid #f59e0b' }}>
                    <div className="card-body p-3">
                        <h6 className="font-weight-bold text-warning mb-3 text-uppercase" style={{ fontSize: '0.8rem' }}>
                            Courier Comparison (Cheapest First)
                        </h6>
                        <div className="row">
                            {sorted.map((c, i) => (
                                <div key={c.label} className="col-md-4 mb-2">
                                    <div className="p-2 rounded text-center" style={{
                                        border: i === 0 ? '2px solid #10b981' : '1px solid #e2e8f0',
                                        backgroundColor: i === 0 ? '#f0fdf4' : '#fafafa'
                                    }}>
                                        {i === 0 && <span className="badge badge-success mb-1" style={{ fontSize: '0.65rem' }}>CHEAPEST</span>}
                                        <p className="mb-0 font-weight-bold text-dark" style={{ fontSize: '0.85rem' }}>{c.label}</p>
                                        <p className="mb-0 font-weight-bold" style={{ fontSize: '1.1rem', color: i === 0 ? '#10b981' : '#1e293b' }}>
                                            AED {money(c.finalPrice, 2)}
                                        </p>
                                        <div className="mt-1" style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                            <div>Freight: AED {money(c.baseFreight, 2)}</div>
                                            <div>Other Charges: AED {money(c.otherCharges, 2)}</div>
                                            {c.dutyEst > 0 && <div>Duty & Taxes: AED {money(c.dutyEst, 2)}</div>}
                                            {c.discountAmount > 0 && (
                                                <div className="text-danger mt-1">
                                                    Discount Applied:<br />
                                                    <span style={{ fontSize: '0.7rem' }}>{c.discountLabel}</span><br />
                                                    <b>- AED {money(c.discountAmount, 2)}</b>
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            );
        })() : null;

        const cards = Object.keys(deliveryTypes).map(deliveryType => {
            const details = deliveryTypes[deliveryType];
            if (!details || Object.keys(details).length === 0) return null;

            const isDoor = deliveryType.includes('Door') || deliveryType.includes('Courier');
            const dest = locations.to?.city || locations.to?.country || '';
            const pkgSummary = summarizePackages(data.products);
            const pkg = pkgSummary.count;
            const dimsStr = pkgSummary.text;

            const isLand = activeTab === 'LAND';
            const isCourier = activeTab === 'COURIER' || deliveryType.includes('Courier');
            const grWt = Number(data.totalWt || 0);
            const vol = Number(data.totalVol || 0);
            const wPct = parseFloat(
                data.marginWeightPct ?? data.margin?.weight_percentage ??
                (isLand || isCourier ? 2 : 3)
            );
            const vPct = parseFloat(
                data.marginVolumePct ?? data.margin?.volume_percentage ??
                (isCourier ? 3 : isLand ? 3 : 10)
            );
            const grWtME = grWt * (1 + wPct / 100);
            const rawVolME = vol * (1 + vPct / 100);
            const volME = isCourier ? truncateTo3(rawVolME) : rawVolME;
            // Courier volumetric kg = CBM × 200 (backend); Land = ×250; else CBM display
            const volWt = isCourier
                ? volME * 200
                : isLand
                    ? volME * 250
                    : volME;
            const chWt = isCourier
                ? courierPackageChargeable(data.products, wPct, vPct)
                : isLand
                    ? landPackageChargeable(data.products, wPct, vPct)
                    : volME;
            const roundOffChWt = roundOffChargeable(chWt, 0.5);

            const goodsValue = Number(data.totalGoodsValue || 0);

            let baseFreight = 0;
            let otherCharges = 0;

            // LAND: always sum explicit TNT keys — API total_amount was historically freight-only
            if (isLand) {
                const landKeys = deliveryType.includes('Consolidation')
                    ? [
                        'fregiht', 'freight', 'pallet_charges', 'handling_in_out_express',
                        'transport_store_to_s2d', 'silla_border', 'batha_border',
                        'final_destination_charge', 'certificate_of_origin'
                    ]
                    : [
                        'be_fee', 'admin_fee', 'transport_store_to_s2d', 'handling_in_out',
                        'fregiht', 'freight', 'fuel_charges', 'peak_surcharge', 'additional_surcharge_amount'
                    ];
                landKeys.forEach((k) => {
                    const v = parseFloat(details[k]);
                    if (!isNaN(v)) baseFreight += v;
                });
            } else if (details.total_amount !== undefined) {
                baseFreight = parseFloat(details.total_amount);
                otherCharges = 0;
            } else {
                Object.entries(details).forEach(([key, value]) => {
                    if (key === 'discountRule') return;
                    if (key.startsWith('discount_') && key.includes('grant_total')) return;
                    if (key.includes('name') || key.includes('_tt')) return;
                    if (key.includes('margin') || key.includes('grant_total') || key === 'grand_total') return;
                    if (['overweightFee', 'dutyCafAmount', 'finalFreight', 'totalDutyEst', 'baseFreight', 'dutyVal', 'vatVal', 'cafPercent', 'port_name', 'courier_caf_fee', 'courier_total_freight', 'courier_margin_pct'].includes(key)) return;

                    const val = parseFloat(value);
                    if (!isNaN(val)) {
                        if (key === 'net_amount' || key === 'base_freight' || key === 'freight') {
                            baseFreight += val;
                        } else {
                            otherCharges += val;
                        }
                    }
                });
            }

            const total = baseFreight + otherCharges;
            let marginPct = details.courier_margin_pct !== undefined
                ? Number(details.courier_margin_pct)
                : 30;
            let minMarginAmt = 0;
            // Prefer API margin amounts when present (backend already applied min %), else Tier tables
            let profitFromApi = undefined;
            if (details.courier_margin_amount !== undefined) {
                profitFromApi = Number(details.courier_margin_amount);
            } else if (isLand && deliveryType.includes('Consolidation') && details.consolidation_margin_amount !== undefined) {
                profitFromApi = Number(details.consolidation_margin_amount);
                if (data.consolidation_profit_margin?.freight_data?.percentage !== undefined) {
                    marginPct = Number(data.consolidation_profit_margin.freight_data.percentage);
                }
            } else if (isLand && details.door_margin_amount !== undefined && !profitMargins?.length) {
                profitFromApi = Number(details.door_margin_amount);
                if (data.door_profit_margin?.freight_data?.percentage !== undefined) {
                    marginPct = Number(data.door_profit_margin.freight_data.percentage);
                }
            }
            if (profitFromApi === undefined && profitMargins && profitMargins.length > 0) {
                const tierMargin = pickTierMargin(profitMargins, selectedTierIndex, total, goodsValue);
                if (tierMargin) {
                    marginPct = tierMargin.marginPct;
                    minMarginAmt = tierMargin.minMarginAmt;
                }
            }

            let profit = profitFromApi !== undefined ? profitFromApi : total * (marginPct / 100);
            if (profitFromApi === undefined && profit < minMarginAmt) {
                profit = minMarginAmt;
            }
            const totalWithMargins = total + profit;
            const roundOff = Math.round(totalWithMargins);

            // Door / Courier: CAF on the Round Off amount. Consolidation: no CAF.
            const mpsCaf = parseFloat(systemSettings?.marketplace_settings?.caf ?? details.cafPercent ?? 5) || 0;
            const doorCaf = computeDoorCaf(totalWithMargins, mpsCaf);
            const caf = isDoor
                ? (details.courier_caf_fee !== undefined ? Number(details.courier_caf_fee) : doorCaf.caf)
                : 0;
            const totalFreight = isDoor
                ? (details.courier_total_freight !== undefined
                    ? Number(details.courier_total_freight)
                    : doorCaf.totalFreight)
                : roundOff;
            const serviceKey = details.packingServiceKey;
            const freightDiff = Number(data.freightDelta?.[serviceKey]) || 0;
            const freightPair = data.freightCompare?.[serviceKey] || null;
            const woodenBox = packingChargeFor(data, serviceKey);
            const battery = data.totalAttributeCost || 0;
            const modeRules = (isLand ? modeChargeRules.land : isCourier ? modeChargeRules.courier : emptyModeRules()) || emptyModeRules();
            const destinationCountry = [locations.to?.country, locations.to?.city, locations.to?.label].filter(Boolean).join(', ');
            const originCharge = (isLand || isCourier)
                ? certificateAmount(modeRules.certificate_of_origin, destinationCountry, goodsValue, roundOffChWt)
                : 0;
            const remoteCharge = isCourier
                ? remoteAmount(modeRules.remote_surcharge, destinationCountry, destinationPostal, roundOffChWt, modeRules.remote_tiers)
                : { amount: 0, tier: '' };
            const reimbursementFor = (dutyAndTaxes) => (
                (isLand || isCourier)
                    ? reimbursementAmount(modeRules.reimbursement, destinationCountry, dutyAndTaxes)
                    : { amount: 0, percent: 0, minimum: 0 }
            );
            const totalFrt = totalFreight + woodenBox + battery + originCharge + (Number(remoteCharge.amount) || 0);

            // Duty & Taxes
            let appliedDuty = details.calculated_duty_est || 0;
            let taxesCalculated = 0;
            let cifValue = 0;
            let insurance1 = 0;
            let freight25 = 0;
            let dutyCaf = 0;
            const addDutyFee = 50;

            const firstItem = data.products?.[0] || {};
            const dynDutyRate = parseFloat(firstItem.dutyRate || firstItem.duty || firstItem.hs_duty) || 12;
            const dynVatRate = parseFloat(firstItem.vatRate || firstItem.vat || firstItem.hs_vat) || 18;

            const mps = systemSettings?.marketplace_settings || {};
            const insRate = parseFloat(mps.duty_tax_insurance_per !== undefined ? mps.duty_tax_insurance_per : 1);
            const freightRate = parseFloat(mps.duty_tax_freight_per !== undefined ? mps.duty_tax_freight_per : 25);
            const cafRate = parseFloat(mps.caf !== undefined ? mps.caf : 5);

            if (appliedDuty === 0 && isDoor && !deliveryType.includes('Port')) {
                insurance1 = goodsValue * (insRate / 100);
                freight25 = goodsValue * (freightRate / 100);
                cifValue = goodsValue + insurance1 + freight25;
                appliedDuty = cifValue * (dynDutyRate / 100);
                taxesCalculated = (cifValue + appliedDuty) * (dynVatRate / 100);
            }

            if (appliedDuty > 0) {
                dutyCaf = (appliedDuty + taxesCalculated) * (cafRate / 100);
            }

            const totalDutyTaxes = appliedDuty + taxesCalculated + dutyCaf + (appliedDuty > 0 ? addDutyFee : 0);
            const apiReimburse = reimbursementFor(Number(data.dutyApiData?.total_duty_and_taxes) || 0);
            const storedReimburse = reimbursementFor((Number(details.dutyVal) || 0) + (Number(details.vatVal) || 0));
            const fallbackReimburse = reimbursementFor(appliedDuty + taxesCalculated);

            const tierNum = selectedTierIndex + 1;
            const marginLabel = `(${marginPct}% Tier ${tierNum})`;

            const RenderRow = ({ label, value, hint, from }) => (
                <div className="border-bottom py-1" style={{ fontSize: '13px' }}>
                    <div className="d-flex justify-content-between align-items-start">
                        <span className="text-secondary" style={{ flex: 1, paddingRight: 8 }}>
                            <ExplainToggle label={label} why={hint} from={from} />
                        </span>
                        <span className="font-weight-bold">{value}</span>
                    </div>
                </div>
            );

            const courierPrefix = (deliveryType.split(' ')[0] || '').toLowerCase();
            const freightAmtForFuel = parseFloat(
                details.fregiht || details.freight ||
                details[`${courierPrefix}_fregiht`] || details[`${courierPrefix}_freight`] || 0
            );
            const courierSurchargeKeys = courierPrefix === 'ups'
                ? ['ups_additional_amount', 'ups_large_package_surcharge_amount', 'ups_over_max_surcharge_amount', 'peak_demand_surcharge']
                : courierPrefix === 'dhl'
                    ? ['additional_surcharge_amount', 'large_package_surcharge_amount', 'over_max_surcharge_amount', 'peak_demand_surcharge']
                    : ['peak_surcharge', 'additional_surcharge_amount', 'over_30_charge', 'additional_shipment_amount', 'large_package_surcharge_amount', 'over_max_surcharge_amount', 'peak_demand_surcharge'];
            const surchargeAmtForFuel = ['fedex', 'ups', 'dhl'].includes(courierPrefix)
                ? courierSurchargeKeys.reduce((sum, key) => sum + (parseFloat(details[key]) || 0), 0)
                : 0;

            const quoteLines = [];
            Object.entries(details).forEach(([key, value]) => {
                if (key === 'discountRule' || key.includes('margin') || key.includes('grant_total') || key === 'grand_total') return;
                if (['overweightFee', 'dutyCafAmount', 'finalFreight', 'totalDutyEst', 'baseFreight', 'dutyVal', 'vatVal', 'cafPercent', 'total_amount', 'calculated_duty_est', 'calculated_overweight_fee', 'calculated_caf_fee', 'port_name', 'courier_caf_fee', 'courier_total_freight', 'courier_margin_pct', 'courier_grant_total'].includes(key)) return;
                const val = parseFloat(value);
                if (!Number.isFinite(val) || val === 0) return;
                const enriched = enrichChargeLabel(key, val, {
                    data, details, freightAmt: freightAmtForFuel, surchargeAmt: surchargeAmtForFuel, courierPrefix, serviceType: deliveryType,
                });
                quoteLines.push({ label: enriched.label, value: money(val, 2) });
            });
            if (originCharge > 0) quoteLines.push({ label: 'Certificate of Origin', value: money(originCharge, 2) });
            if (Number(remoteCharge.amount) > 0) quoteLines.push({ label: `Remote Surcharge (Tier ${remoteCharge.tier})`, value: money(remoteCharge.amount, 2) });
            const dutyTotal = data.dutyApiData && data.dutyApiData.status
                ? Number(data.dutyApiData.duty_estimate) + apiReimburse.amount
                : totalDutyTaxes > 0
                    ? totalDutyTaxes + fallbackReimburse.amount
                    : 0;
            const buildQuote = () => ({
                title: deliveryType.replace(/_/g, ' '),
                mode: activeTab,
                from: locations.from?.description || locations.from?.formatted_address || 'Origin',
                to: locations.to?.description || locations.to?.formatted_address || dest || 'Destination',
                lines: quoteLines,
                beforeProfit: money(total, 2),
                profit: money(profit, 2),
                roundOff: String(roundOff),
                freight: money(totalFrt, 2),
                duty: dutyTotal > 0 ? money(dutyTotal, 2) : 'At actuals',
                date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
            });
            const openQuote = () => setQuoteDoc(buildQuote());

            return (
                <div key={deliveryType} className="card mb-4 shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <div className="card-header text-white font-weight-bold d-flex justify-content-between align-items-center flex-wrap" style={{ backgroundColor: '#2563eb', gap: 8 }}>
                        <span className="text-uppercase">{deliveryType.replace(/_/g, ' ')} Breakdown</span>
                        <span className="d-flex align-items-center" style={{ gap: 8 }}>
                            <button type="button" className="btn btn-sm btn-light" onClick={openQuote}>View quotation</button>
                            <button type="button" className="btn btn-sm btn-outline-light" onClick={() => shareQuote(buildQuote())}>Share</button>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: 'auto', display: 'inline-block', fontWeight: 'bold', marginLeft: '10px' }}
                            value={selectedTierIndex}
                            onChange={(e) => setSelectedTierIndex(Number(e.target.value))}
                        >
                            {TIERS.map((t, idx) => (
                                <option key={idx} value={idx}>
                                    {t.label}
                                </option>
                            ))}
                        </select>
                        </span>
                    </div>
                    <div className="card-body p-0 bg-light">
                        <div className="row m-0">
                            {/* Meta Details */}
                            <div className="col-md-3 p-3 border-right border-bottom bg-white">
                                <h6 className="font-weight-bold text-primary mb-3">Meta Details</h6>
                                <RenderRow label="Mode" value={activeTab} />
                                <RenderRow label="Dest" value={dest || 'Destination'} />
                                <RenderRow label="Service Type" value={deliveryType.replace(/_/g, ' ')} />
                                <RenderRow label="Pkg" value={pkg} />
                                <RenderRow label="Dims" value={dimsStr} />
                                <RenderRow label="Gr.Wt" value={money(grWt, 2)} />
                                <RenderRow label={`Gr.Wt with M/E (${wPct}%)`} value={money(grWtME, 2)} hint={`${grWt} × (1 + ${wPct}%)`} from="Margin settings: weight margin % on the gross weight of every package." />
                                <RenderRow label="Vol" value={money(vol, 2)} />
                                {(isLand || isCourier) && <RenderRow label={`Vol with M/E (${vPct}%)`} value={money(volME, 3)} hint={`${vol} × (1 + ${vPct}%)${isCourier ? ', cut to 3 decimals' : ''}`} from="Margin settings: volume margin % on the CBM of every package." />}
                                {(isLand || isCourier) && (
                                    <RenderRow
                                        label="Vol Wt"
                                        value={money(volWt, 2)}
                                        hint={isCourier ? `Vol M/E × 200` : `Vol M/E × 250`}
                                        from={isCourier ? 'Courier volumetric rule: CBM with margin × 200 kg.' : 'Land volumetric rule: CBM with margin × 250 kg.'}
                                    />
                                )}
                                <RenderRow
                                    label={isLand || isCourier ? 'Ch Wt' : 'Chargeable Vol'}
                                    value={isLand || isCourier ? money(chWt, 2) : money(chWt, 3)}
                                    hint={isCourier || isLand ? 'Each package uses the greater of its weight with margin and its volume kg. Those package weights are added.' : `Vol × (1 + ${vPct}%)`}
                                    from={isCourier || isLand ? 'Each package is compared on its own, then the higher figure from every package is added.' : 'Chargeable volume is the CBM after the volume margin.'}
                                />
                                {(isLand || isCourier) && <RenderRow label="Round Off Ch Wt" value={roundOffChWt} hint="Rounded to the nearest kg" from="The kg figure used to pick the zone or weight band and to price per-kg freight." />}
                                <RenderRow label="Goods Value" value={money(goodsValue, 2)} />
                            </div>

                            {/* Calculations */}
                            <div className="col-md-5 p-3 border-right border-bottom bg-white">
                                <h6 className="font-weight-bold text-success mb-3">Calculations Breakdown</h6>
                                {(() => {
                                    const breakdownRows = [];
                                    const isCourierCard = ['fedex', 'ups', 'dhl'].includes(courierPrefix);
                                    const courierGroup = (key) => {
                                        const name = String(key || '').toLowerCase();
                                        if (!isCourierCard) return 0;
                                        if (name.includes('govt')) return 4;
                                        if (name.includes('fuel')) return 3;
                                        if (['additional_surcharge_amount', 'ups_additional_amount', 'large_package_surcharge_amount', 'ups_large_package_surcharge_amount', 'over_max_surcharge_amount', 'ups_over_max_surcharge_amount', 'peak_surcharge', 'peak_demand_surcharge', 'over_30_charge', 'additional_shipment_amount'].includes(name)) return 2;
                                        return 0;
                                    };
                                    Object.entries(details).forEach(([key, value]) => {
                                        if (key === 'discountRule') return;
                                        if (key.startsWith('discount_') && key.includes('grant_total')) return;
                                        const grouped = courierGroup(key);
                                        if (!grouped && (key.includes('name') || key.includes('_tt') || key.includes('id') || key.includes('_id') || key === 'tier_name')) return;
                                        if (key.includes('margin') || key.includes('grant_total') || key === 'grand_total') return;
                                        if (['overweightFee', 'dutyCafAmount', 'finalFreight', 'totalDutyEst', 'baseFreight', 'dutyVal', 'vatVal', 'cafPercent', 'total_amount', 'calculated_duty_est', 'calculated_overweight_fee', 'calculated_caf_fee', 'port_name', 'courier_caf_fee', 'courier_total_freight', 'courier_margin_pct', 'courier_grant_total'].includes(key)) return;

                                        const val = parseFloat(value);
                                        if (isNaN(val)) return;
                                        const enriched = enrichChargeLabel(key, val, {
                                            data,
                                            details,
                                            freightAmt: freightAmtForFuel,
                                            surchargeAmt: surchargeAmtForFuel,
                                            courierPrefix,
                                            serviceType: deliveryType,
                                            chargeableKg: roundOffChWt,
                                            chargeableCbm: volME,
                                        });
                                        breakdownRows.push({ ...enriched, val, sort: grouped, index: breakdownRows.length });
                                    });
                                    const chargeGroups = [[], [], [], [], []];
                                    breakdownRows.forEach((row) => {
                                        chargeGroups[row.sort] = chargeGroups[row.sort] || [];
                                        chargeGroups[row.sort].push(row);
                                    });
                                    const orderedRows = isCourierCard
                                        ? [...chargeGroups[0], ...chargeGroups[2], ...chargeGroups[3], ...chargeGroups[4]]
                                        : breakdownRows;
                                    return orderedRows.map((r, i) => (
                                        <RenderRow key={i} label={r.label} value={money(r.val, 2)} hint={r.hint} from={r.from} />
                                    ));
                                })()}

                                {details.calculated_overweight_fee > 0 && <RenderRow label="Overweight Fee" value={money(details.calculated_overweight_fee, 2)} />}

                                <div className="d-flex justify-content-between py-2 border-top mt-1 px-1" style={{ fontSize: '13px', backgroundColor: '#fef3c7', borderRadius: 4 }}>
                                    <span className="font-weight-bold" style={{ color: '#92400e' }}>Total (Before Profit)</span>
                                    <span className="font-weight-bold" style={{ color: '#92400e' }}>{money(total, 2)}</span>
                                </div>
                                <RenderRow
                                    label="Profit"
                                    value={`${money(profit, 2)} ${marginLabel}`}
                                    hint={`${marginPct}% × ${money(total, 2)}${minMarginAmt > 0 ? ` (min ${minMarginAmt})` : ''}`}
                                    from="Tier profit table. The band is chosen from the total before profit and the goods value."
                                />
                                <div className="d-flex justify-content-between border-bottom py-1" style={{ fontSize: '13px', backgroundColor: '#f0fdf4' }}>
                                    <span className="text-success font-weight-bold">Total with Margins</span>
                                    <span className="text-success font-weight-bold">{money(totalWithMargins, 2)}</span>
                                </div>
                                <div className="d-flex justify-content-between py-2 px-1 mb-1" style={{ fontSize: '13px', backgroundColor: '#dbeafe', borderRadius: 4 }}>
                                    <span className="font-weight-bold" style={{ color: '#1e40af' }}>Round Off</span>
                                    <span className="font-weight-bold" style={{ color: '#1e40af' }}>{roundOff}</span>
                                </div>
                                {caf > 0 && (
                                    <RenderRow
                                        label={`CAF (${mpsCaf}%)`}
                                        value={money(caf, 2)}
                                        hint={`${mpsCaf}% × Round Off ${roundOff} · Door only`}
                                    />
                                )}
                                {(isDoor || isCourier) && <RenderRow label="Total Freight" value={money(totalFreight, 2)} />}
                                {originCharge > 0 && (
                                    <RenderRow
                                        label="Certificate of Origin"
                                        value={money(originCharge, 2)}
                                        hint="Charged when goods value or chargeable weight is above the country limit."
                                        from={isLand ? 'Land Mode Charges.' : 'Courier Mode Charges.'}
                                    />
                                )}
                                {Number(remoteCharge.amount) > 0 && (
                                    <RenderRow
                                        label={`Remote Surcharge (Tier ${remoteCharge.tier})`}
                                        value={money(remoteCharge.amount, 2)}
                                        hint={`AED ${remoteCharge.perKg}/kg or minimum AED ${remoteCharge.minimum}, on round off chargeable weight.`}
                                        from="Courier Mode Charges. The destination postal code matches this tier."
                                    />
                                )}
                                {renderPackingAndAttributeRows(data, false, freightDiff, freightPair)}
                                <div className="d-flex justify-content-between py-2 mt-2 border-top" style={{ fontSize: '15px' }}>
                                    <span className="text-dark font-weight-bold">Total Frt</span>
                                    <span className="text-danger font-weight-bold">{money(totalFrt, 2)}</span>
                                </div>
                            </div>

                            {/* Duty/Taxes Calculation */}
                            <div className="col-md-4 p-3 border-bottom bg-white">
                                <h6 className="font-weight-bold text-info mb-3">Duty/Taxes Calculation</h6>
                                {data.dutyApiData && data.dutyApiData.status ? (
                                    <>
                                        <RenderRow label="Cost of Goods" value={money(data.dutyApiData.total_goods_value)} />
                                        <RenderRow label={`Insurance (${parseFloat(systemSettings?.marketplace_settings?.duty_tax_insurance_per ?? 1)}%)`} value={money(data.dutyApiData.insurance_cost)} />
                                        <RenderRow label={`Freight (${parseFloat(systemSettings?.marketplace_settings?.duty_tax_freight_per ?? 25)}%)`} value={money(data.dutyApiData.freight_cost)} />
                                        <RenderRow label="CIF Value" value={money(data.dutyApiData.cif_value)} />
                                        <RenderRow label={`Duty (${parseFloat((data.products?.[0]?.dutyRate || data.products?.[0]?.duty || data.products?.[0]?.hs_duty) || 0)}%)`} value={money(data.dutyApiData.duty_amount)} />
                                        <RenderRow label={`Taxes (${parseFloat((data.products?.[0]?.vatRate || data.products?.[0]?.vat || data.products?.[0]?.hs_vat) || 0)}%)`} value={money(data.dutyApiData.tax_amount)} />
                                        <RenderRow label="Duty + Taxes" value={money(data.dutyApiData.total_duty_and_taxes)} />
                                        <RenderRow
                                            label={`CAF on Duty (${parseFloat(systemSettings?.marketplace_settings?.caf ?? 5)}%)`}
                                            value={money(data.dutyApiData.caf_amount)}
                                            hint={`${parseFloat(systemSettings?.marketplace_settings?.caf ?? 5)}% × (Duty + Taxes)`}
                                        />
                                        <RenderRow label="Additional Duty Fee" value={money(data.dutyApiData.additional_fee)} />
                                        {apiReimburse.amount > 0 && (
                                            <RenderRow
                                                label={`Reimbursement (${apiReimburse.percent}% or min ${money(apiReimburse.minimum, 2)})`}
                                                value={money(apiReimburse.amount, 2)}
                                                hint="The higher of the percent of duty and taxes, or the country minimum."
                                                from={isLand ? 'Land Mode Charges.' : 'Courier Mode Charges.'}
                                            />
                                        )}
                                        <div className="d-flex justify-content-between py-2 mt-2 px-1" style={{ fontSize: '15px', backgroundColor: '#dcfce7', borderRadius: 4 }}>
                                            <span className="font-weight-bold" style={{ color: '#166534' }}>Total Duty & Taxes</span>
                                            <span className="font-weight-bold" style={{ color: '#166534' }}>{money(Number(data.dutyApiData.duty_estimate) + apiReimburse.amount)}</span>
                                        </div>
                                    </>
                                ) : details && details.totalDutyEst !== undefined && details.totalDutyEst > 0 ? (
                                    <>
                                        <RenderRow label="Cost of Goods" value={money(goodsValue, 2)} />
                                        <RenderRow label="Overweight Fee" value={money((details.overweightFee || 0), 2)} />
                                        <RenderRow label="Duty Value" value={money((details.dutyVal || 0), 4)} />
                                        <RenderRow label="Taxes Value (VAT)" value={money((details.vatVal || 0), 4)} />
                                        <RenderRow label="Duty + Taxes" value={money(((details.dutyVal || 0) + (details.vatVal || 0)), 4)} />
                                        <RenderRow label={`CAF on Duty/Taxes (${(details.cafPercent || 0)}%)`} value={money((details.dutyCafAmount || 0), 4)} />
                                        <RenderRow label="Additional Duty Fee" value={money((0), 2)} />
                                        {storedReimburse.amount > 0 && (
                                            <RenderRow
                                                label={`Reimbursement (${storedReimburse.percent}% or min ${money(storedReimburse.minimum, 2)})`}
                                                value={money(storedReimburse.amount, 2)}
                                                hint="The higher of the percent of duty and taxes, or the country minimum."
                                                from={isLand ? 'Land Mode Charges.' : 'Courier Mode Charges.'}
                                            />
                                        )}
                                        <div className="d-flex justify-content-between py-2 mt-2 px-1" style={{ fontSize: '15px', backgroundColor: '#dcfce7', borderRadius: 4 }}>
                                            <span className="font-weight-bold" style={{ color: '#166534' }}>Total Duty & Taxes</span>
                                            <span className="font-weight-bold" style={{ color: '#166534' }}>{money((Number(details.totalDutyEst) || 0) + storedReimburse.amount, 2)}</span>
                                        </div>
                                    </>
                                ) : totalDutyTaxes > 0 ? (
                                    <>
                                        <RenderRow label="Cost of Goods" value={money(goodsValue, 2)} />
                                        <RenderRow label={`Insurance (${insRate}%)`} value={money(insurance1, 2)} />
                                        <RenderRow label={`Freight (${freightRate}%)`} value={money(freight25, 2)} />
                                        <RenderRow label="CIF Value" value={money(cifValue, 2)} />
                                        <RenderRow label={`Duty (${dynDutyRate}%)`} value={money(appliedDuty, 2)} />
                                        <RenderRow label={`Taxes (${dynVatRate}%)`} value={money(taxesCalculated, 2)} />
                                        <RenderRow label="Duty + Taxes" value={money((appliedDuty + taxesCalculated), 2)} />
                                        <RenderRow label="Caf" value={money(dutyCaf, 2)} />
                                        <RenderRow label="Additional Duty Fee" value={money(addDutyFee, 2)} />
                                        {fallbackReimburse.amount > 0 && (
                                            <RenderRow
                                                label={`Reimbursement (${fallbackReimburse.percent}% or min ${money(fallbackReimburse.minimum, 2)})`}
                                                value={money(fallbackReimburse.amount, 2)}
                                                hint="The higher of the percent of duty and taxes, or the country minimum."
                                                from={isLand ? 'Land Mode Charges.' : 'Courier Mode Charges.'}
                                            />
                                        )}
                                        <div className="d-flex justify-content-between py-2 mt-2 px-1" style={{ fontSize: '15px', backgroundColor: '#dcfce7', borderRadius: 4 }}>
                                            <span className="font-weight-bold" style={{ color: '#166534' }}>Total Duty & Taxes</span>
                                            <span className="font-weight-bold" style={{ color: '#166534' }}>{money(totalDutyTaxes + fallbackReimburse.amount, 2)}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-muted font-italic text-center mt-4">
                                        Receiver to pay At Actuals on Arrival
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        });

        return <>{courierBanner}{cards}</>;
    };


    return (
        <div className="container-fluid" style={{ paddingBottom: '100px' }}>
            <h4 className="mb-4">Admin Rate Checker</h4>
            {!isSubmitted && (
                <>
                    <div className="card mb-4 shadow-sm">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label className="font-weight-bold">From</label>
                                        <AutoComplete prefillState={locations.from} getData={(data) => handleLocationChange('from', data)} />
                                    </div>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <div className="form-group">
                                        <label className="font-weight-bold">To (postal code, city and country)</label>
                                        <AutoComplete prefillState={locations.to} getData={(data) => handleLocationChange('to', data)} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {products.map((prod, pIdx) => (
                        <div key={pIdx} className="card mb-4 shadow-sm border-0" style={{ backgroundColor: '#f8fafc', borderRadius: '12px', borderLeft: '4px solid #10b981' }}>
                            <div className="card-header bg-white border-bottom-0 pt-4 pb-0 d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 text-success font-weight-bold">
                                    <span className="badge badge-success mr-2 p-2" style={{ borderRadius: '8px' }}>{pIdx + 1}</span>
                                    Product {pIdx + 1}
                                </h5>
                                {products.length > 1 && (
                                    <button className="btn btn-sm btn-outline-danger" onClick={() => removeProduct(pIdx)}>
                                        &times; Remove Product
                                    </button>
                                )}
                            </div>
                            <div className="card-body">
                                {/* Product Header Row */}
                                <div className="row mb-4">
                                    <div className="col-md-3">
                                        <label className="text-muted small font-weight-bold text-uppercase">Product Name <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control" placeholder="e.g. Carpet" value={prod.name} onChange={(e) => handleProductChange(pIdx, 'name', e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="text-muted small font-weight-bold text-uppercase">HS Code <span className="text-danger">*</span></label>
                                        <div className="input-group">
                                            <input type="text" className={`form-control ${prod.hsStatus === 'valid' ? 'is-valid' : prod.hsStatus === 'invalid' ? 'is-invalid' : ''}`} placeholder="HS Code" value={prod.hsCode} onChange={(e) => handleProductChange(pIdx, 'hsCode', e.target.value)} onBlur={() => validateHsCode(pIdx)} />
                                        </div>
                                        {prod.hsStatus === 'loading' && <small className="text-info">Validating...</small>}
                                        {prod.hsStatus === 'valid' && <small className="text-success">{prod.hsValidationMsg}</small>}
                                        {prod.hsStatus === 'invalid' && <small className="text-danger">{prod.hsValidationMsg}</small>}
                                    </div>
                                    <div className="col-md-2">
                                        <label className="text-muted small font-weight-bold text-uppercase">Quantity <span className="text-danger">*</span></label>
                                        <input type="number" className="form-control" placeholder="1" value={prod.quantity} onChange={(e) => handleProductChange(pIdx, 'quantity', e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label className="text-muted small font-weight-bold text-uppercase">Total Value (AED) <span className="text-danger">*</span></label>
                                        <input type="number" className="form-control" placeholder="0.00" value={prod.totalValue} onChange={(e) => handleProductChange(pIdx, 'totalValue', e.target.value)} />
                                    </div>
                                    <div className="col-md-3">
                                        <label className="text-muted small font-weight-bold text-uppercase">Country of Origin <span className="text-danger">*</span></label>
                                        <select className="form-control" value={prod.coo} onChange={(e) => handleProductChange(pIdx, 'coo', e.target.value)}>
                                            <option value="">Select Country</option>
                                            {countries.map((c, i) => <option key={i} value={c.country_name || c.name}>{c.country_name || c.name}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Toggles Row */}
                                <div className="d-flex flex-wrap mb-4 p-3" style={{ backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fef3c7', gap: '12px' }}>
                                    {[
                                        { key: 'perfume', label: 'Perfume' },
                                        { key: 'battery', label: 'Contains Battery' },
                                        { key: 'cosmetic', label: 'Cosmetics' },
                                        { key: 'magnet', label: 'Contains Magnet' }
                                    ].map(toggle => (
                                        <div key={toggle.key} className="d-flex align-items-center justify-content-between bg-white px-3 py-2" style={{ borderRadius: '8px', flex: '1 1 0%', minWidth: '150px', border: '1px solid #fef3c7' }}>
                                            <span className="font-weight-bold" style={{ color: '#b45309', fontSize: '0.85rem' }}>{toggle.label}</span>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0, position: 'relative' }}>
                                                <input type="checkbox" checked={prod[toggle.key]} onChange={(e) => handleProductChange(pIdx, toggle.key, e.target.checked)} style={{ opacity: 0, position: 'absolute', zIndex: -1 }} />
                                                <div style={{ width: '36px', height: '20px', backgroundColor: prod[toggle.key] ? '#b45309' : '#e2e8f0', borderRadius: '10px', position: 'relative', transition: 'all 0.3s' }}>
                                                    <div style={{ width: '16px', height: '16px', backgroundColor: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: prod[toggle.key] ? '18px' : '2px', transition: 'all 0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
                                                </div>
                                            </label>
                                        </div>
                                    ))}
                                </div>

                                {/* MSDS Row */}
                                {(prod.perfume || prod.battery || prod.cosmetic || prod.magnet) && (
                                    <div className="row mb-4 mx-0 p-3 bg-white" style={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div className="col-12">
                                            <label className="text-dark small font-weight-bold text-uppercase mb-3">Material Safety Data Sheet (MSDS)</label>
                                            <div className="d-flex mb-3 gap-3">
                                                <div className="custom-control custom-radio custom-control-inline">
                                                    <input type="radio" id={`msds-link-${pIdx}`} name={`msdsType-${pIdx}`} className="custom-control-input" checked={prod.msdsType === 'link'} onChange={() => handleProductChange(pIdx, 'msdsType', 'link')} />
                                                    <label className="custom-control-label" htmlFor={`msds-link-${pIdx}`}>Provide Link</label>
                                                </div>
                                                <div className="custom-control custom-radio custom-control-inline">
                                                    <input type="radio" id={`msds-file-${pIdx}`} name={`msdsType-${pIdx}`} className="custom-control-input" checked={prod.msdsType === 'file'} onChange={() => handleProductChange(pIdx, 'msdsType', 'file')} />
                                                    <label className="custom-control-label" htmlFor={`msds-file-${pIdx}`}>Upload File</label>
                                                </div>
                                            </div>
                                            {prod.msdsType === 'link' ? (
                                                <input type="text" className="form-control" placeholder="https://example.com/msds.pdf" value={prod.msdsLink} onChange={(e) => handleProductChange(pIdx, 'msdsLink', e.target.value)} />
                                            ) : (
                                                <input type="file" className="form-control-file" onChange={(e) => handleProductChange(pIdx, 'msdsFile', e.target.files[0])} />
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Packages Section */}
                                <div className="mt-4">
                                    <h6 className="text-success font-weight-bold text-uppercase mb-3" style={{ borderLeft: '4px solid #10b981', paddingLeft: '8px' }}>Packages ({(prod.packages || []).length} package{(prod.packages || []).length > 1 ? 's' : ''})</h6>

                                    {(prod.packages || []).map((pkg, pkgIdx) => (
                                        <div key={pkgIdx} className="p-3 mb-3 bg-white" style={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <span className="text-muted small font-weight-bold text-uppercase">Package {pkgIdx + 1}</span>
                                                {prod.packages.length > 1 && (
                                                    <button className="btn btn-sm btn-link text-danger p-0" onClick={() => removePackage(pIdx, pkgIdx)}>&times; Remove</button>
                                                )}
                                            </div>
                                            <div className="row">
                                                <div className="col-md-2 mb-3">
                                                    <label className="text-dark small font-weight-bold">No. of Pkgs*</label>
                                                    <input type="number" className="form-control form-control-sm" value={pkg.count} onChange={(e) => handlePackageChange(pIdx, pkgIdx, 'count', e.target.value)} />
                                                </div>
                                                <div className="col-md-3 mb-3">
                                                    <label className="text-dark small font-weight-bold">Weight/Pkg* (kg)</label>
                                                    <input type="number" className="form-control form-control-sm" value={pkg.weight} onChange={(e) => handlePackageChange(pIdx, pkgIdx, 'weight', e.target.value)} />
                                                </div>
                                                <div className="col-md-7 mb-3">
                                                    <label className="text-dark small font-weight-bold">Dimensions L x W x H* (cms)</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input type="number" className="form-control form-control-sm text-center" placeholder="L" value={pkg.length} onChange={(e) => handlePackageChange(pIdx, pkgIdx, 'length', e.target.value)} style={{ borderLeft: '3px solid #10b981' }} />
                                                        <span className="text-muted mx-2">x</span>
                                                        <input type="number" className="form-control form-control-sm text-center" placeholder="W" value={pkg.width} onChange={(e) => handlePackageChange(pIdx, pkgIdx, 'width', e.target.value)} />
                                                        <span className="text-muted mx-2">x</span>
                                                        <input type="number" className="form-control form-control-sm text-center" placeholder="H" value={pkg.height} onChange={(e) => handlePackageChange(pIdx, pkgIdx, 'height', e.target.value)} />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-2">
                                                <label className="text-dark small font-weight-bold text-uppercase mb-2">Packaging Type</label>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {['Packaging not required', 'Wooden Box', 'Pallet Packaging', 'Carton Packaging', 'Crate Packaging'].map(type => (
                                                        <div key={type} className="d-flex align-items-center mr-3 mb-2 px-2 py-1" style={{ border: '1px solid #e2e8f0', borderRadius: '4px', backgroundColor: pkg.packaging_type === type ? '#f0fdf4' : 'transparent', borderColor: pkg.packaging_type === type ? '#10b981' : '#e2e8f0' }}>
                                                            <input type="radio" id={`pkg-${pIdx}-${pkgIdx}-${type.replace(/\s+/g, '-')}`} name={`packagingType-${pIdx}-${pkgIdx}`} checked={pkg.packaging_type === type} onChange={() => handlePackageChange(pIdx, pkgIdx, 'packaging_type', type)} className="mr-2" style={{ cursor: 'pointer' }} />
                                                            <label className="m-0" style={{ fontSize: '0.8rem', color: pkg.packaging_type === type ? '#065f46' : '#64748b', cursor: 'pointer' }} htmlFor={`pkg-${pIdx}-${pkgIdx}-${type.replace(/\s+/g, '-')}`}>{type}</label>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    <button className="btn btn-block" style={{ border: '1px dashed #10b981', color: '#10b981', backgroundColor: '#f0fdf4', fontWeight: 'bold' }} onClick={() => addPackage(pIdx)}>
                                        + ADD ANOTHER PACKAGE
                                    </button>

                                    <div className="d-flex justify-content-between align-items-center mt-3 px-3 py-2" style={{ backgroundColor: '#f0fdf4', borderRadius: '8px', fontSize: '0.85rem' }}>
                                        <span className="font-weight-bold text-success">Pkgs: {(prod.packages || []).reduce((acc, curr) => acc + (parseInt(curr?.count) || 0), 0)}</span>
                                        <span className="font-weight-bold text-dark">Weight: {money((prod.packages || []).reduce((acc, curr) => acc + ((parseFloat(curr?.weight) || 0) * (parseInt(curr?.count) || 0)), 0), 2)} kg</span>
                                        <span className="font-weight-bold text-dark">Volume: {money((prod.packages || []).reduce((acc, curr) => acc + (((parseFloat(curr?.length) || 0) * (parseFloat(curr?.width) || 0) * (parseFloat(curr?.height) || 0) / 1000000) * (parseInt(curr?.count) || 0)), 0), 2)} CBM</span>
                                    </div>
                                    {(() => {
                                        const selectedTypes = [...new Set(
                                            (prod.packages || [])
                                                .map((pkg) => pkg.packaging_type)
                                                .filter((type) => type && type !== 'Packaging not required')
                                        )];
                                        if (selectedTypes.length === 0) return null;
                                        return (
                                            <div className="mt-2 d-flex flex-wrap align-items-center" style={{ gap: '6px' }}>
                                                <span className="small text-muted font-weight-bold">Packing:</span>
                                                {selectedTypes.map((type) => (
                                                    <span key={type} className="px-2 py-1" style={{ backgroundColor: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, color: '#065f46' }}>
                                                        {type}
                                                    </span>
                                                ))}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    ))}

                    <button className="btn btn-outline-success font-weight-bold mb-4 px-4 py-2" style={{ borderRadius: '20px' }} onClick={addProduct}>
                        + ADD NEW PRODUCT
                    </button>

                    <div className="text-right mb-5">
                        <button className="btn btn-primary btn-lg px-5 font-weight-bold" onClick={() => {
                            const placeFilled = (loc) => loc && (loc.city || loc.country || loc.description);
                            const missing = [];
                            if (!placeFilled(locations.from)) missing.push('From');
                            if (!placeFilled(locations.to)) missing.push('To');
                            products.forEach((prod, index) => {
                                const label = `Product ${index + 1}`;
                                if (!String(prod.name || '').trim()) missing.push(`${label} name`);
                                if (!String(prod.hsCode || '').trim()) missing.push(`${label} HS code`);
                                if (!(Number(prod.quantity) > 0)) missing.push(`${label} quantity`);
                                if (prod.totalValue === '' || prod.totalValue == null) missing.push(`${label} total value`);
                                if (!prod.coo) missing.push(`${label} country of origin`);
                                (prod.packages || []).forEach((pkg, pkgIndex) => {
                                    const pkgLabel = `${label} package ${pkgIndex + 1}`;
                                    if (!(Number(pkg.count) > 0)) missing.push(`${pkgLabel} number of packages`);
                                    if (!(Number(pkg.weight) > 0)) missing.push(`${pkgLabel} weight`);
                                    if (!(Number(pkg.length) > 0) || !(Number(pkg.width) > 0) || !(Number(pkg.height) > 0)) missing.push(`${pkgLabel} dimensions`);
                                });
                            });
                            if (missing.length) {
                                window.alert(`Fill these before checking rates:\n${missing.join('\n')}`);
                                return;
                            }
                            const loadModeRules = (mode) => DataService.get('/check/mode_charges', { mode }).then((res) => {
                                const grouped = Array.isArray(res?.data?.reimbursement) ? res.data : res?.data?.data;
                                return grouped && Array.isArray(grouped.reimbursement) ? grouped : emptyModeRules();
                            }).catch(() => emptyModeRules());
                            Promise.all([loadModeRules('courier'), loadModeRules('land')]).then(([courier, land]) => {
                                setModeChargeRules({ courier, land });
                                setIsSubmitted(true);
                            });
                        }} style={{ borderRadius: '8px' }}>Save & Continue to Rates</button>
                    </div>
                </>
            )}

            {isSubmitted && (
                <>
                    <div className="card mb-4 shadow-sm border-0" style={{ borderRadius: '12px', borderLeft: '4px solid #10b981' }}>
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="font-weight-bold text-dark m-0">Summary</h5>
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => { setIsSubmitted(false); setResults({}); setActiveTab(null); }}>Edit Details</button>
                            </div>
                            <div className="row">
                                <div className="col-md-3">
                                    <div className="text-secondary" style={{ fontSize: '10px' }}>FROM</div>
                                    <div className="font-weight-bold" style={{ fontSize: '13px' }}>
                                        {locations.from && locations.from.city ? `${locations.from.city}${locations.from.country ? `, ${locations.from.country}` : ''}` : 'Select Origin'}
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <p className="text-muted small text-uppercase mb-1">To</p>
                                    <p className="font-weight-bold text-dark m-0">{locations.to && (locations.to.city || locations.to.country) ? `${locations.to.city || ''}${locations.to.city && locations.to.country ? ', ' : ''}${locations.to.country || ''}` : 'Not provided'}</p>
                                </div>
                                <div className="col-md-2">
                                    <p className="text-muted small text-uppercase mb-1">Packages</p>
                                    <p className="font-weight-bold text-dark m-0">
                                        {products.reduce((acc, p) => acc + (p?.packages || []).reduce((sum, pkg) => sum + (parseInt(pkg?.count) || 0), 0), 0)} Pkgs
                                    </p>
                                </div>
                                <div className="col-md-2">
                                    <p className="text-muted small text-uppercase mb-1">Total Weight</p>
                                    <p className="font-weight-bold text-dark m-0">
                                        {money(products.reduce((acc, p) => acc + (p?.packages || []).reduce((sum, pkg) => sum + (parseInt(pkg?.count) || 0) * (parseFloat(pkg?.weight) || 0), 0), 0), 2)} kg
                                    </p>
                                </div>
                                <div className="col-md-2">
                                    <p className="text-muted small text-uppercase mb-1">Total Volume</p>
                                    <p className="font-weight-bold text-dark m-0">
                                        {money(products.reduce((acc, p) => acc + (p?.packages || []).reduce((sum, pkg) => sum + (parseInt(pkg?.count) || 0) * (((parseFloat(pkg?.length) || 0) * (parseFloat(pkg?.width) || 0) * (parseFloat(pkg?.height) || 0)) / 1000000), 0), 0), 2)} CBM
                                    </p>
                                </div>
                            </div>
                            {(() => {
                                const selectedTypes = [...new Set(
                                    products.flatMap((prod) =>
                                        (prod.packages || [])
                                            .map((pkg) => pkg.packaging_type)
                                            .filter((type) => type && type !== 'Packaging not required')
                                    )
                                )];
                                if (selectedTypes.length === 0) return null;
                                return (
                                    <div className="mt-3 d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                                        <span className="small text-muted font-weight-bold text-uppercase">Packing:</span>
                                        {selectedTypes.map((type) => (
                                            <span key={type} className="px-2 py-1" style={{ backgroundColor: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, color: '#065f46' }}>
                                                {type}
                                            </span>
                                        ))}
                                    </div>
                                );
                            })()}
                            <hr style={{ margin: '20px 0', borderColor: '#e2e8f0' }} />
                            <h6 className="font-weight-bold text-dark mb-3">Product Details</h6>
                            {products.map((prod, pIdx) => {
                                const toggles = [];
                                if (prod.perfume) toggles.push('Perfume');
                                if (prod.battery) toggles.push('Battery');
                                if (prod.cosmetic) toggles.push('Cosmetics');
                                if (prod.magnet) toggles.push('Magnet');

                                const pkgCount = (prod.packages || []).reduce((sum, pkg) => sum + (parseInt(pkg?.count) || 0), 0);
                                const pkgWeight = money((prod.packages || []).reduce((sum, pkg) => sum + (parseInt(pkg?.count) || 0) * (parseFloat(pkg?.weight) || 0), 0), 2);
                                const selectedTypes = [...new Set(
                                    (prod.packages || [])
                                        .map((pkg) => pkg.packaging_type)
                                        .filter((type) => type && type !== 'Packaging not required')
                                )];

                                return (
                                    <div key={pIdx} className="mb-3 p-3" style={{ backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div className="row">
                                            <div className="col-md-3">
                                                <p className="text-muted small text-uppercase mb-1">Product {pIdx + 1}</p>
                                                <p className="font-weight-bold text-dark m-0">{prod.name || '-'}</p>
                                            </div>
                                            <div className="col-md-2">
                                                <p className="text-muted small text-uppercase mb-1">HS Code</p>
                                                <p className="font-weight-bold text-dark m-0">{prod.hsCode || '-'}</p>
                                            </div>
                                            <div className="col-md-2">
                                                <p className="text-muted small text-uppercase mb-1">Value (AED)</p>
                                                <p className="font-weight-bold text-dark m-0">{prod.totalValue || '0.00'}</p>
                                            </div>
                                            <div className="col-md-2">
                                                <p className="text-muted small text-uppercase mb-1">Origin</p>
                                                <p className="font-weight-bold text-dark m-0">{prod.coo || '-'}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="text-muted small text-uppercase mb-1">Contains</p>
                                                <p className="font-weight-bold text-danger m-0">{toggles.length > 0 ? toggles.join(', ') : 'None'}</p>
                                            </div>
                                        </div>
                                        <div className="mt-2 pt-2 border-top d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                                            <span className="small text-muted font-weight-bold">Packaging:</span>
                                            <span className="small text-dark font-weight-bold">{pkgCount} Pkgs | {pkgWeight} kg Total</span>
                                            {selectedTypes.map((type) => (
                                                <span key={type} className="px-2 py-1" style={{ backgroundColor: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 600, color: '#065f46' }}>
                                                    {type}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="card shadow-sm mb-5">
                        <div className="card-body">
                            <div className="d-flex flex-wrap gap-2 justify-content-center">
                                <button id="btn-fetch-AIR" className="btn btn-info mr-2 px-4 font-weight-bold" onClick={() => fetchRates('AIR')} disabled={loading === 'AIR'}>
                                    {loading === 'AIR' ? <><span className="spinner-border spinner-border-sm mr-2" /> Loading...</> : 'Get Air Rates'}
                                </button>
                                <button id="btn-fetch-SEA" className="btn btn-primary mr-2 px-4 font-weight-bold" onClick={() => fetchRates('SEA')} disabled={loading === 'SEA'}>
                                    {loading === 'SEA' ? <><span className="spinner-border spinner-border-sm mr-2" /> Loading...</> : 'Get Sea Rates'}
                                </button>
                                <button id="btn-fetch-COURIER" className="btn btn-success mr-2 px-4 font-weight-bold" onClick={() => fetchRates('COURIER')} disabled={loading === 'COURIER'}>
                                    {loading === 'COURIER' ? <><span className="spinner-border spinner-border-sm mr-2" /> Loading...</> : 'Get Courier Rates'}
                                </button>
                                <button id="btn-fetch-LAND" className="btn btn-warning px-4 font-weight-bold" onClick={() => fetchRates('LAND')} disabled={loading === 'LAND'}>
                                    {loading === 'LAND' ? <><span className="spinner-border spinner-border-sm mr-2" /> Loading...</> : 'Get Land Rates'}
                                </button>
                            </div>

                            {Object.keys(results).length > 0 && (
                                <div className="mt-5">
                                    <ul className="nav nav-tabs mb-4 border-bottom-0">
                                        {['AIR', 'SEA', 'COURIER', 'LAND'].map(mode => (
                                            results[mode] && (
                                                <li className="nav-item mr-2" key={mode} style={{ cursor: 'pointer' }}>
                                                    <a
                                                        className={`nav-link rounded font-weight-bold ${activeTab === mode ? 'active bg-primary text-white' : 'bg-light text-dark'}`}
                                                        onClick={() => setActiveTab(mode)}
                                                    >
                                                        {mode} QUOTES
                                                    </a>
                                                </li>
                                            )
                                        ))}
                                    </ul>

                                    <div className="tab-content">
                                        {['AIR', 'SEA', 'COURIER', 'LAND'].map(mode => (
                                            activeTab === mode && results[mode] && (
                                                <QuoteErrorBoundary key={mode} resetKey={`${mode}-${loading || ''}`}>
                                                    <div className="tab-pane active">
                                                        {(() => {
                                                            try {
                                                                if (mode === 'AIR') return renderAirResultTable(results[mode]);
                                                                if (mode === 'SEA') return renderSeaResultTable(results[mode]);
                                                                return renderResultTable(results[mode]);
                                                            } catch (error) {
                                                                return <div className="alert alert-warning mt-3 mb-0">This quote could not be shown for the selected location. Change the location or run the rate again. The rest of the page stays available.</div>;
                                                            }
                                                        })()}
                                                    </div>
                                                </QuoteErrorBoundary>
                                            )
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}
            {quoteDoc && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setQuoteDoc(null)}>
                    <div style={{ background: '#fff', width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto', borderRadius: 16, padding: 28 }} onClick={(event) => event.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <div style={{ fontSize: 12, letterSpacing: 1, color: '#038559', fontWeight: 800 }}>WORLD2DOOR</div>
                                <h3 style={{ margin: '6px 0 0', fontSize: 22 }}>Freight quotation</h3>
                                <div style={{ color: '#64748b', fontSize: 13 }}>{quoteDoc.date}</div>
                            </div>
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setQuoteDoc(null)}>Close</button>
                        </div>
                        <div style={{ marginTop: 18, padding: 14, background: '#f8fafc', borderRadius: 10 }}>
                            <div style={{ fontWeight: 800 }}>{quoteDoc.title}</div>
                            <div style={{ color: '#475569', fontSize: 14 }}>{quoteDoc.mode}</div>
                            <div style={{ color: '#334155', marginTop: 6 }}>{quoteDoc.from}</div>
                            <div style={{ color: '#334155' }}>to {quoteDoc.to}</div>
                        </div>
                        <table style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse' }}>
                            <tbody>
                                {quoteDoc.lines.map((line) => (
                                    <tr key={line.label} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '8px 0', color: '#334155' }}>{line.label}</td>
                                        <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 700 }}>AED {line.value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ marginTop: 16, background: '#f0fdf4', borderRadius: 10, padding: 14 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}><span>Freight total</span><span>AED {quoteDoc.freight}</span></div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}><span>Duty and taxes</span><span>{quoteDoc.duty === 'At actuals' ? 'At actuals' : `AED ${quoteDoc.duty}`}</span></div>
                        </div>
                        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                            <button type="button" className="btn btn-primary" onClick={() => shareQuote(quoteDoc)}>Share / print</button>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => navigator.clipboard.writeText(quoteText(quoteDoc))}>Copy</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

